// Per-type IVR node config forms. Each form renders the schema-matched
// fields for one IvrNodeType; a generic JSON textarea is the last-resort
// fallback for types with no typed form yet. Keep field shapes in sync
// with the backend Zod schemas in backend/src/ivrNodes/service.ts.

import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import type { IvrNodeType, IvrNodeRow, RotationStatus } from '@/api/ivrNodes';
import { ivrNodes } from '@/api/ivrNodes';
import { recordingFolders } from '@/api/recordingFolders';
import { voicemails } from '@/api/voicemails';
import { conferenceRooms } from '@/api/conferenceRooms';
import { extensions } from '@/api/extensions';
import { ringGroups } from '@/api/ringGroups';
import { surveys } from '@/api/surveys';
import { quizzes } from '@/api/quizzes';
import { courses } from '@/api/courses';
import { paymentGateways } from '@/api/paymentGateways';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Popover, PopoverContent, PopoverTrigger,
} from '@/components/ui/popover';
import {
  Command, CommandEmpty, CommandInput, CommandItem, CommandList,
} from '@/components/ui/command';
import { Trash2, Plus, Clock, ExternalLink, Check, ChevronsUpDown, X } from 'lucide-react';
import { authLists } from '@/api/authLists';
import { formatRef } from '@/lib/formatRef';
import { cn } from '@/lib/cn';
import { NodeTypeBadge } from './NodeTypeBadge';
import { labelsApi } from '@/api/labels';
import { useCustomerPicker } from '@/components/CustomerPicker';
import { DateTimePicker } from '@/components/DateTimePicker';

// ── Inline create helpers ────────────────────────────────────────────────────

/**
 * CreateFolderInline — a small toggleable form that creates a recording
 * folder and calls onCreate(newId) when done.
 */
function CreateFolderInline({
  onCreate,
}: {
  onCreate: (id: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const qc = useQueryClient();

  async function submit() {
    const trimmed = name.trim();
    if (!trimmed) return;
    setSaving(true);
    setErr(null);
    try {
      const row = await recordingFolders.create({ name: trimmed });
      qc.invalidateQueries({ queryKey: ['recordingFolders', 'all'] });
      onCreate(row.id);
      setOpen(false);
      setName('');
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Create failed');
    } finally {
      setSaving(false);
    }
  }

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="flex items-center gap-1.5 text-xs text-indigo-600 hover:text-indigo-700 mt-1"
      >
        <Plus className="h-3 w-3" />
        New folder
      </button>
    );
  }

  return (
    <div className="mt-1 flex flex-col gap-1.5 rounded-md border border-indigo-100 bg-indigo-50/40 p-2.5">
      <p className="text-[10px] font-semibold uppercase tracking-widest text-indigo-400">
        New folder
      </p>
      <input
        autoFocus
        type="text"
        value={name}
        onChange={(e) => setName(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); submit(); } if (e.key === 'Escape') setOpen(false); }}
        placeholder="Folder name…"
        className="h-7 rounded border border-border bg-white px-2 text-xs focus:outline-none focus:ring-1 focus:ring-indigo-400"
      />
      {err && <p className="text-[10px] text-destructive">{err}</p>}
      <div className="flex gap-1.5">
        <button
          type="button"
          onClick={submit}
          disabled={saving || !name.trim()}
          className="rounded bg-indigo-600 px-2.5 py-1 text-[11px] font-medium text-white hover:bg-indigo-700 disabled:opacity-40"
        >
          {saving ? 'Creating…' : 'Create'}
        </button>
        <button
          type="button"
          onClick={() => { setOpen(false); setName(''); setErr(null); }}
          className="rounded px-2.5 py-1 text-[11px] text-slate-500 hover:bg-slate-100"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

/**
 * CreateVoicemailBoxInline — same pattern for voicemail boxes.
 */
function CreateVoicemailBoxInline({ onCreate }: { onCreate: (id: string) => void }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const qc = useQueryClient();

  async function submit() {
    const trimmed = name.trim();
    if (!trimmed) return;
    setSaving(true);
    setErr(null);
    try {
      const row = await voicemails.createBox({ name: trimmed, owner_kind: 'account' });
      qc.invalidateQueries({ queryKey: ['voicemail-boxes', 'picker'] });
      onCreate(row.id);
      setOpen(false);
      setName('');
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Create failed');
    } finally {
      setSaving(false);
    }
  }

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="flex items-center gap-1.5 text-xs text-indigo-600 hover:text-indigo-700 mt-1"
      >
        <Plus className="h-3 w-3" />
        New voicemail box
      </button>
    );
  }

  return (
    <div className="mt-1 flex flex-col gap-1.5 rounded-md border border-indigo-100 bg-indigo-50/40 p-2.5">
      <p className="text-[10px] font-semibold uppercase tracking-widest text-indigo-400">
        New voicemail box
      </p>
      <input
        autoFocus
        type="text"
        value={name}
        onChange={(e) => setName(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); submit(); } if (e.key === 'Escape') setOpen(false); }}
        placeholder="Voicemail box name…"
        className="h-7 rounded border border-border bg-white px-2 text-xs focus:outline-none focus:ring-1 focus:ring-indigo-400"
      />
      {err && <p className="text-[10px] text-destructive">{err}</p>}
      <div className="flex gap-1.5">
        <button
          type="button"
          onClick={submit}
          disabled={saving || !name.trim()}
          className="rounded bg-indigo-600 px-2.5 py-1 text-[11px] font-medium text-white hover:bg-indigo-700 disabled:opacity-40"
        >
          {saving ? 'Creating…' : 'Create'}
        </button>
        <button
          type="button"
          onClick={() => { setOpen(false); setName(''); setErr(null); }}
          className="rounded px-2.5 py-1 text-[11px] text-slate-500 hover:bg-slate-100"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

// ── Shared Field wrapper (mirrors the one in NodeEditorDrawer) ───────────────

function Field({
  label, htmlFor, required, hint, error, children,
}: {
  label: string;
  htmlFor?: string;
  required?: boolean;
  hint?: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1">
      <Label htmlFor={htmlFor} className="text-xs font-medium text-slate-600">
        {label}
        {required && <span className="ml-0.5 text-red-500" aria-hidden>*</span>}
      </Label>
      {children}
      {hint && <p className="text-[11px] text-slate-400">{hint}</p>}
      {error && <p role="alert" className="text-[11px] text-red-500">{error}</p>}
    </div>
  );
}

// ── Helpers ──────────────────────────────────────────────────────────────────

type Cfg = Record<string, unknown>;

function getStr(cfg: Cfg, key: string, fallback = ''): string {
  const v = cfg[key];
  return typeof v === 'string' ? v : fallback;
}
function getNum(cfg: Cfg, key: string, fallback?: number): number | undefined {
  const v = cfg[key];
  if (typeof v === 'number') return v;
  return fallback;
}
function getBool(cfg: Cfg, key: string, fallback = false): boolean {
  const v = cfg[key];
  return typeof v === 'boolean' ? v : fallback;
}

// ── Folder picker (replaces raw UUID inputs) ────────────────────────────────

/** Searchable dropdown that loads all recording folders from the API. */
function FolderSelectField({
  folderId,
  onChange,
  optional = false,
  id = 'cfg-folder-id',
}: {
  folderId: string;
  onChange: (id: string) => void;
  optional?: boolean;
  id?: string;
}) {
  const navigate = useNavigate();
  // Scope the list to the current customer context. Without this,
  // super_admin / reseller_owner sessions see folders from every
  // tenant they have visibility into — a cross-tenant leak inside
  // the IVR node editor (Sam, 2026-05-19).
  const { effectiveCustomerId } = useCustomerPicker(null, () => {}, {});
  const { data, isLoading } = useQuery({
    queryKey: ['recordingFolders', 'all', effectiveCustomerId ?? ''],
    queryFn: () => recordingFolders.list({
      limit: 500,
      customer_id: effectiveCustomerId ?? undefined,
    }),
    staleTime: 30_000,
  });
  const folders = data?.rows ?? [];
  const selected = folders.find((f) => f.id === folderId);

  return (
    <div className="space-y-1">
      <Select
        value={folderId || (optional ? '__none__' : '')}
        onValueChange={(v) => onChange(v === '__none__' ? '' : v)}
      >
        <SelectTrigger id={id} className="h-8 text-sm">
          <SelectValue placeholder={isLoading ? 'Loading…' : 'Pick a folder…'} />
        </SelectTrigger>
        <SelectContent>
          {optional && (
            <SelectItem value="__none__" className="text-sm">
              <span className="italic text-slate-400">— none —</span>
            </SelectItem>
          )}
          {folders.map((f) => (
            <SelectItem key={f.id} value={f.id} className="text-sm">
              <span className="flex items-center gap-2">
                <span className="font-mono text-[11px] text-slate-400">{f.ref_number}</span>
                {f.name}
              </span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {selected && (
        <button
          type="button"
          onClick={() => navigate(`/folders/FLD-${selected.ref_number}`)}
          className="inline-flex items-center gap-1 text-xs text-slate-400 hover:text-indigo-600 cursor-pointer transition-colors"
        >
          <ExternalLink className="h-3 w-3 shrink-0" />
          <span>Open {selected.name}</span>
        </button>
      )}
    </div>
  );
}

// ── Label picker ────────────────────────────────────────────────────────────

/** Searchable dropdown that loads caller labels from the API. */
function LabelSelectField({
  value,
  onChange,
  id,
  required,
}: {
  value: string;
  onChange: (id: string) => void;
  id?: string;
  required?: boolean;
}) {
  const { effectiveCustomerId } = useCustomerPicker(null, () => {}, {});
  const { data, isLoading } = useQuery({
    queryKey: ['labels', 'picker', effectiveCustomerId],
    queryFn: () =>
      labelsApi.list(effectiveCustomerId ? { customerId: effectiveCustomerId } : undefined),
    staleTime: 30_000,
  });
  const rows = data?.rows ?? [];
  return (
    <Select value={value || '__none__'} onValueChange={(v) => onChange(v === '__none__' ? '' : v)}>
      <SelectTrigger id={id} className="h-8 text-sm">
        <SelectValue placeholder={isLoading ? 'Loading…' : 'Pick a label…'} />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="__none__" className="text-sm text-muted-foreground">
          — pick a label —
        </SelectItem>
        {rows.map((l) => (
          <SelectItem key={l.id} value={l.id} className="text-sm">
            <span className="flex items-center gap-2">
              {l.color && (
                <span
                  className="inline-block h-2.5 w-2.5 rounded-full flex-shrink-0"
                  style={{ backgroundColor: l.color }}
                />
              )}
              {l.name}
            </span>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

// ── Content & navigation ─────────────────────────────────────────────────────

function FolderConfig({ config, onChange, nField = false }: {
  config: Cfg;
  onChange: (c: Cfg) => void;
  nField?: boolean;
}) {
  const folderId = getStr(config, 'folder_id');
  const n = getNum(config, 'n', 5);

  return (
    <div className="space-y-3">
      <Field label="Recording folder" htmlFor="cfg-folder-id" required>
        <FolderSelectField
          folderId={folderId}
          onChange={(id) => onChange({ ...config, folder_id: id })}
          id="cfg-folder-id"
        />
      </Field>
      <CreateFolderInline onCreate={(id) => onChange({ ...config, folder_id: id })} />
      {nField && (
        <Field label="How many items (N)" htmlFor="cfg-folder-n" hint="Default 5, max 100">
          <Input
            id="cfg-folder-n"
            type="number"
            min={1}
            max={100}
            value={n ?? 5}
            onChange={(e) => onChange({ ...config, n: Number(e.target.value) })}
            className="h-8 w-28 font-mono text-sm"
          />
        </Field>
      )}
    </div>
  );
}

export function SeriesFolderConfig({ config, onChange }: { config: Cfg; onChange: (c: Cfg) => void }) {
  const folderId = getStr(config, 'folder_id');

  return (
    <div className="space-y-3">
      <Field
        label="Recording folder"
        htmlFor="cfg-series-folder-id"
        required
      >
        <FolderSelectField
          folderId={folderId}
          onChange={(id) => onChange({ ...config, folder_id: id })}
          id="cfg-series-folder-id"
        />
      </Field>
      <CreateFolderInline
        onCreate={(id) => onChange({ ...config, folder_id: id })}
      />

      <div className="rounded-md bg-sky-50/60 border border-sky-100 px-3 py-2.5 text-xs text-sky-700">
        If no main recording is uploaded, the system automatically announces the available range (1&ndash;N).
        Upload a Main Recording on the node to use your own custom prompt instead.
      </div>
    </div>
  );
}

export function SequentialFolderConfig({ config, onChange }: { config: Cfg; onChange: (c: Cfg) => void }) {
  const folderId = getStr(config, 'folder_id');
  const direction = getStr(config, 'direction', 'forward');

  return (
    <div className="space-y-3">
      <Field
        label="Folder"
        htmlFor="cfg-seq-folder-id"
        required
      >
        <FolderSelectField
          folderId={folderId}
          onChange={(id) => onChange({ ...config, folder_id: id })}
          id="cfg-seq-folder-id"
        />
      </Field>
      <CreateFolderInline
        onCreate={(id) => onChange({ ...config, folder_id: id })}
      />

      <Field label="Direction" htmlFor="cfg-seq-direction">
        <Select
          value={direction}
          onValueChange={(v) => onChange({ ...config, direction: v })}
        >
          <SelectTrigger id="cfg-seq-direction" className="h-8 text-sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="forward" className="text-sm">Forward — plays onward after chosen recording</SelectItem>
            <SelectItem value="backward" className="text-sm">Backward — plays back toward the first recording</SelectItem>
          </SelectContent>
        </Select>
      </Field>

      <div className="rounded-md bg-sky-50/60 border border-sky-100 px-3 py-2.5 text-xs text-sky-700">
        If no main recording is uploaded, the system automatically announces the available range (1&ndash;N).
        Upload a Main Recording on the node to use your own prompt. Playlist then auto-advances in the chosen
        direction after selection. # skips ahead, 9 goes back, * exits.
      </div>
    </div>
  );
}

export function BulletinConfig({ config, onChange }: { config: Cfg; onChange: (c: Cfg) => void }) {
  const folderId = getStr(config, 'folder_id');
  const playOrder = getStr(config, 'play_order', 'folder_order');

  return (
    <div className="space-y-3">
      <Field
        label="Recording folder"
        htmlFor="cfg-bulletin-folder-id"
        required
      >
        <FolderSelectField
          folderId={folderId}
          onChange={(id) => onChange({ ...config, folder_id: id })}
          id="cfg-bulletin-folder-id"
        />
      </Field>
      <CreateFolderInline
        onCreate={(id) => onChange({ ...config, folder_id: id })}
      />

      <Field label="Play order" htmlFor="cfg-bulletin-order">
        <Select
          value={playOrder}
          onValueChange={(v) => onChange({ ...config, play_order: v })}
        >
          <SelectTrigger id="cfg-bulletin-order" className="h-8 text-sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="folder_order" className="text-sm">Folder order (top to bottom)</SelectItem>
            <SelectItem value="newest_first" className="text-sm">Newest first</SelectItem>
          </SelectContent>
        </Select>
      </Field>

      <div className="rounded-md bg-blue-50/60 border border-blue-100 px-3 py-2.5 text-xs text-blue-700">
        Navigation: 9 ← previous &nbsp;•&nbsp; # → next &nbsp;•&nbsp; * exit.
        Position is remembered for the session. Standard playback controls apply while listening.
      </div>
    </div>
  );
}
export function LatestNFolderConfig(p: { config: Cfg; onChange: (c: Cfg) => void }) {
  // Per 2026-04-29: latest_n_folder now plays only the single most-recent
  // recording with full playback DTMF. The legacy `n` field is dropped.
  return <FolderConfig {...p} />;
}

// ── Unlock-folder forms (shared skip-day toggle) ─────────────────────────────

const UNLOCK_DAY_KEYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const;
type UnlockDay = typeof UNLOCK_DAY_KEYS[number];

function todayYmd(): string {
  return new Date().toISOString().slice(0, 10);
}

function UnlockSchedulingFields({
  config,
  onChange,
  countId,
  dateId,
}: {
  config: Cfg;
  onChange: (c: Cfg) => void;
  countId: string;
  dateId: string;
}) {
  const unlockedCount = getNum(config, 'unlocked_count', 1) ?? 1;
  const unlockedDate = getStr(config, 'unlocked_date') || todayYmd();
  const skipDays: UnlockDay[] = Array.isArray(config.skip_days_of_week)
    ? (config.skip_days_of_week as UnlockDay[]).filter((d) => UNLOCK_DAY_KEYS.includes(d))
    : [];

  function toggleSkipDay(day: UnlockDay) {
    const set = new Set(skipDays);
    if (set.has(day)) set.delete(day);
    else set.add(day);
    const next = UNLOCK_DAY_KEYS.filter((d) => set.has(d));
    onChange({ ...config, skip_days_of_week: next });
  }

  return (
    <>
      <Field
        label="Currently unlocked"
        htmlFor={countId}
      >
        <input
          id={countId}
          type="number"
          min={0}
          step={1}
          value={unlockedCount}
          onChange={(e) => {
            const v = parseInt(e.target.value, 10);
            onChange({ ...config, unlocked_count: isNaN(v) ? 0 : Math.max(0, v) });
          }}
          className="h-8 w-24 rounded border border-slate-300 px-2 text-sm"
        />
      </Field>
      <Field
        label="As of date"
        htmlFor={dateId}
      >
        <DateTimePicker
          value={unlockedDate ? `${unlockedDate}T00:00` : ''}
          onChange={(v) => onChange({ ...config, unlocked_date: v ? v.slice(0, 10) : todayYmd() })}
          defaultTimeOnDateSelect="00:00"
          placeholder={todayYmd()}
        />
        <p className="mt-1 text-[11px] text-slate-500">
          Defaults to today. Set a future date to delay the schedule until that day.
        </p>
      </Field>
      <div className="space-y-1.5">
        <p className="text-xs font-medium text-slate-600">
          Skip days
          <span className="ml-1 font-normal text-slate-400 text-[11px]">
            — no auto-unlock on these days of the week.
          </span>
        </p>
        <div className="flex flex-wrap gap-1">
          {UNLOCK_DAY_KEYS.map((d) => {
            const active = skipDays.includes(d);
            return (
              <button
                key={d}
                type="button"
                onClick={() => toggleSkipDay(d)}
                className={
                  'rounded border px-2 py-1 text-[11px] font-mono uppercase transition-colors ' +
                  (active
                    ? 'border-indigo-300 bg-indigo-50 text-indigo-700'
                    : 'border-slate-200 bg-white text-slate-500 hover:bg-slate-50')
                }
              >
                {d}
              </button>
            );
          })}
        </div>
      </div>
    </>
  );
}

export function UnlockSeriesFolderConfig({ config, onChange }: { config: Cfg; onChange: (c: Cfg) => void }) {
  const folderId = getStr(config, 'folder_id');

  return (
    <div className="space-y-3">
      <Field
        label="Recording folder"
        htmlFor="cfg-unlock-series-folder-id"
        required
      >
        <FolderSelectField
          folderId={folderId}
          onChange={(id) => onChange({ ...config, folder_id: id })}
          id="cfg-unlock-series-folder-id"
        />
      </Field>
      <CreateFolderInline
        onCreate={(id) => onChange({ ...config, folder_id: id })}
      />

      <UnlockSchedulingFields
        config={config}
        onChange={onChange}
        countId="cfg-unlock-series-count"
        dateId="cfg-unlock-series-date"
      />

      <div className="rounded-md bg-violet-50/60 border border-violet-100 px-3 py-2.5 text-xs text-violet-700">
        Caller picks a position number; only unlocked positions are valid choices.
        Each non-skipped calendar day past the &quot;as of&quot; date adds +1 unlock.
        During playback: full controls (1/4/7 rewind, 3/6/9 forward, 5/8 speed, 2 pause, 0 bookmark).
        * returns to picker; # is a no-op.
      </div>
    </div>
  );
}

export function UnlockSequentialFolderConfig({ config, onChange }: { config: Cfg; onChange: (c: Cfg) => void }) {
  const folderId = getStr(config, 'folder_id');
  const direction = getStr(config, 'direction', 'forward');

  return (
    <div className="space-y-3">
      <Field
        label="Recording folder"
        htmlFor="cfg-unlock-seq-folder-id"
        required
      >
        <FolderSelectField
          folderId={folderId}
          onChange={(id) => onChange({ ...config, folder_id: id })}
          id="cfg-unlock-seq-folder-id"
        />
      </Field>
      <CreateFolderInline
        onCreate={(id) => onChange({ ...config, folder_id: id })}
      />

      <Field label="Direction" htmlFor="cfg-unlock-seq-direction">
        <Select
          value={direction}
          onValueChange={(v) => onChange({ ...config, direction: v })}
        >
          <SelectTrigger id="cfg-unlock-seq-direction" className="h-8 text-sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="forward" className="text-sm">Forward — auto-advances toward the last unlocked recording</SelectItem>
            <SelectItem value="backward" className="text-sm">Backward — auto-advances toward the first recording</SelectItem>
          </SelectContent>
        </Select>
      </Field>

      <UnlockSchedulingFields
        config={config}
        onChange={onChange}
        countId="cfg-unlock-seq-count"
        dateId="cfg-unlock-seq-date"
      />

      <div className="rounded-md bg-violet-50/60 border border-violet-100 px-3 py-2.5 text-xs text-violet-700">
        Caller picks a starting position; recordings auto-advance from there. Stops
        at the boundary of unlocked items (the next would be locked) and returns to
        the picker. * returns to picker during playback; # is a no-op (no manual skip).
      </div>
    </div>
  );
}

const SKIP_DAYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const;
type SkipDay = typeof SKIP_DAYS[number];

/**
 * Searchable picker over the rotation folder's items — "Start today from…".
 * Options come from the rotation-status endpoint (engine play order), so
 * position numbers always match what callers hear. Follows the
 * TreeNodeCombobox Popover+Command pattern.
 */
function RotationStartCombobox({
  items, selectedPosition, playingPosition, onPick,
}: {
  items: RotationStatus['items'];
  /** 1-based position currently chosen in the form (pending or saved). */
  selectedPosition: number;
  /** 1-based position playing today per the SAVED config. */
  playingPosition: number | null;
  onPick: (position: number) => void;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const selected = items.find((it) => it.position === selectedPosition) ?? null;

  const q = search.trim().toLowerCase();
  const filtered = q
    ? items.filter((it) =>
        it.recording_name.toLowerCase().includes(q)
        || String(it.position).includes(q))
    : items;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="h-8 w-full justify-between px-2 text-sm font-normal"
        >
          <span className="flex min-w-0 items-center gap-1.5 truncate">
            {selected ? (
              <>
                <span className="shrink-0 font-mono text-[11px] text-slate-500">
                  {selected.position} of {items.length}
                </span>
                <span className="truncate">{selected.recording_name}</span>
              </>
            ) : (
              <span className="truncate text-slate-400">Pick an item…</span>
            )}
          </span>
          <ChevronsUpDown className="ms-1.5 h-3.5 w-3.5 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[320px] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Search by name or position…"
            value={search}
            onValueChange={setSearch}
          />
          <CommandList className="max-h-56">
            {filtered.length === 0 && (
              <CommandEmpty>No matching recording.</CommandEmpty>
            )}
            {filtered.map((it) => (
              <CommandItem
                key={it.position}
                value={String(it.position)}
                onSelect={() => {
                  onPick(it.position);
                  setOpen(false);
                  setSearch('');
                }}
              >
                <Check
                  className={cn(
                    'me-2 h-3.5 w-3.5 shrink-0',
                    it.position === selectedPosition ? 'opacity-100' : 'opacity-0',
                  )}
                />
                <span className="flex min-w-0 items-center gap-1.5">
                  <span className="shrink-0 font-mono text-[11px] text-slate-500">{it.position}</span>
                  <span className="truncate">{it.recording_name}</span>
                  {it.position === playingPosition && (
                    <span className="shrink-0 rounded bg-teal-50 px-1 py-px text-[10px] font-medium text-teal-600">
                      today
                    </span>
                  )}
                </span>
              </CommandItem>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

/**
 * "Rotation position" block — shows which item plays today ("item 5 of 12")
 * and lets the admin restart the rotation from any item. Reads the SAVED
 * config via GET /ivr/nodes/:id/rotation-status; picking an item writes
 * `rotation_offset` into the form config, so the drawer's normal Save
 * persists it. The saved node's updated_at is part of the status query key,
 * so a save (which invalidates ['ivrNode', nodeId]) refreshes the readout.
 */
function RotationPositionBlock({
  nodeId, config, onChange,
}: {
  nodeId?: string;
  config: Cfg;
  onChange: (c: Cfg) => void;
}) {
  const formFolderId = getStr(config, 'folder_id');
  const formOffset = getNum(config, 'rotation_offset', 0) ?? 0;

  // Shared with NodeEditorDrawer's own query (same key) — invalidated on
  // every save, which rolls updated_at forward and re-keys the status query.
  const { data: savedNode } = useQuery({
    queryKey: ['ivrNode', nodeId],
    queryFn: () => ivrNodes.get(nodeId!),
    enabled: !!nodeId,
  });
  const savedCfg = (savedNode?.config ?? null) as Record<string, unknown> | null;
  const savedFolderId = typeof savedCfg?.folder_id === 'string' ? savedCfg.folder_id : '';

  const { data: status, isLoading, isError } = useQuery({
    queryKey: ['ivrNodeRotationStatus', nodeId, savedNode?.updated_at ?? ''],
    queryFn: () => ivrNodes.rotationStatus(nodeId!),
    enabled: !!nodeId && !!savedNode,
  });

  let body: React.ReactNode;
  if (!nodeId) {
    body = (
      <p className="text-[11px] text-slate-400">
        Save the node first — the rotation position appears once the node exists.
      </p>
    );
  } else if (formFolderId && savedNode && savedFolderId !== formFolderId) {
    body = (
      <p className="text-[11px] text-slate-400">
        Folder changed — save the node to see its rotation position.
      </p>
    );
  } else if (isLoading || !status) {
    body = (
      <p className="text-[11px] text-slate-400">
        {isError ? 'Could not load the rotation position.' : 'Loading rotation position…'}
      </p>
    );
  } else if (status.total === 0 || status.position === null || status.natural_position === null) {
    body = (
      <p className="text-[11px] text-slate-400">
        This folder has no active recordings yet. Add recordings to the folder
        to start the rotation.
      </p>
    );
  } else {
    const { total, position, natural_position: naturalPosition } = status;
    // What the picker should show as chosen: the saved natural position
    // shifted by the FORM's offset — reflects an unsaved pick immediately.
    const selectedIdx0 = (((naturalPosition - 1 + formOffset) % total) + total) % total;
    const pending = formOffset !== status.offset;

    body = (
      <div className="space-y-2">
        <div className="rounded-md border border-slate-200 bg-slate-50/60 px-3 py-2">
          <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-400">
            Playing today
          </p>
          <p className="mt-0.5 text-sm text-slate-700">
            <span className="font-mono text-[12px] text-slate-500">
              item {position} of {total}
            </span>
            <span className="mx-1.5 text-slate-300" aria-hidden>—</span>
            {status.recording_name}
          </p>
        </div>

        <Field
          label="Start today from"
          htmlFor="cfg-rotation-start-from"
          hint="Picks which item plays today; the rotation continues from there, one item per rotation cycle."
        >
          <RotationStartCombobox
            items={status.items}
            selectedPosition={selectedIdx0 + 1}
            playingPosition={position}
            onPick={(pickedPosition) => {
              const newOffset =
                ((((pickedPosition - 1) - (naturalPosition - 1)) % total) + total) % total;
              onChange({ ...config, rotation_offset: newOffset });
            }}
          />
        </Field>
        {pending && (
          <p className="text-[11px] text-amber-600">
            Item {selectedIdx0 + 1} plays today after you save.
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-1.5">
      <p className="text-xs font-medium text-slate-600">Rotation position</p>
      {body}
    </div>
  );
}

export function RotationFolderConfig({ config, onChange, nodeId }: { config: Cfg; onChange: (c: Cfg) => void; nodeId?: string }) {
  const folderId = getStr(config, 'folder_id');
  const rotationInterval = getNum(config, 'rotation_interval_days', 1) ?? 1;
  const skipDays: SkipDay[] = Array.isArray(config.skip_days_of_week)
    ? (config.skip_days_of_week as SkipDay[]).filter((d) => SKIP_DAYS.includes(d))
    : [];

  function toggleSkipDay(day: SkipDay) {
    const set = new Set(skipDays);
    if (set.has(day)) set.delete(day);
    else set.add(day);
    const next = SKIP_DAYS.filter((d) => set.has(d));
    onChange({ ...config, skip_days_of_week: next.length ? next : [] });
  }

  return (
    <div className="space-y-3">
      <Field
        label="Recording folder"
        htmlFor="cfg-rotation-folder-id"
        required
      >
        <FolderSelectField
          folderId={folderId}
          onChange={(id) => onChange({ ...config, folder_id: id })}
          id="cfg-rotation-folder-id"
        />
      </Field>
      <CreateFolderInline
        onCreate={(id) => onChange({ ...config, folder_id: id })}
      />

      <Field label="Rotation interval" htmlFor="cfg-rotation-interval">
        <Select
          value={String(rotationInterval)}
          onValueChange={(v) => onChange({ ...config, rotation_interval_days: Number(v) })}
        >
          <SelectTrigger id="cfg-rotation-interval" className="h-8 text-sm w-52">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="1" className="text-sm">Daily</SelectItem>
            <SelectItem value="2" className="text-sm">Every 2 days</SelectItem>
            <SelectItem value="3" className="text-sm">Every 3 days</SelectItem>
            <SelectItem value="7" className="text-sm">Weekly</SelectItem>
            <SelectItem value="14" className="text-sm">Bi-weekly</SelectItem>
            <SelectItem value="30" className="text-sm">Monthly</SelectItem>
          </SelectContent>
        </Select>
      </Field>

      <div className="space-y-1.5">
        <p className="text-xs font-medium text-slate-600">
          Skip days
          <span className="ml-1 font-normal text-slate-400 text-[11px]">
            — on skipped days, the previous day&apos;s recording continues to play.
          </span>
        </p>
        <div className="flex flex-wrap gap-1">
          {SKIP_DAYS.map((d) => {
            const active = skipDays.includes(d);
            return (
              <button
                key={d}
                type="button"
                onClick={() => toggleSkipDay(d)}
                className={
                  'rounded border px-2 py-1 text-[11px] font-mono uppercase transition-colors ' +
                  (active
                    ? 'border-indigo-300 bg-indigo-50 text-indigo-700'
                    : 'border-slate-200 bg-white text-slate-500 hover:bg-slate-50')
                }
              >
                {d}
              </button>
            );
          })}
        </div>
      </div>

      <RotationPositionBlock nodeId={nodeId} config={config} onChange={onChange} />

      <div className="rounded-md bg-teal-50/60 border border-teal-100 px-3 py-2.5 text-xs text-teal-700">
        Plays one recording from the folder per rotation cycle. The active recording advances
        automatically based on the date — every caller hears the same item on a given day.
        After the last item, the rotation starts again from item 1.
      </div>
    </div>
  );
}

export function RandomFolderConfig(p: { config: Cfg; onChange: (c: Cfg) => void }) {
  return <FolderConfig {...p} />;
}

export function BookmarkConfig({ config, onChange }: { config: Cfg; onChange: (c: Cfg) => void }) {
  const slot = getStr(config, 'slot', '1');
  return (
    <Field
      label="Bookmark slot"
      htmlFor="cfg-bookmark-slot"
      hint='"0" is reserved for auto-bookmarks. Use "1" through "9" for explicit saves.'
    >
      <Select value={slot} onValueChange={(v) => onChange({ ...config, slot: v })}>
        <SelectTrigger id="cfg-bookmark-slot" className="h-8 w-28 text-sm font-mono">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map((s) => (
            <SelectItem key={s} value={s} className="font-mono text-sm">{s}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </Field>
  );
}

export function ReturnToBookmarkConfig() {
  return (
    <div className="rounded-md bg-blue-50/60 border border-blue-100 px-3 py-2.5 text-xs text-blue-700">
      Prompts the caller for a slot (1–9) and resumes the recording
      bookmarked there. No configuration required.
    </div>
  );
}

// ── Input & logic ────────────────────────────────────────────────────────────

export function DataCollectionConfig({ config, onChange }: { config: Cfg; onChange: (c: Cfg) => void }) {
  const fieldKey = getStr(config, 'field_key');
  return (
    <div className="space-y-3">
      <Field
        label="Field key"
        htmlFor="cfg-dc-field"
        required
        hint="Column name under which the captured digits are stored (e.g. zip_code)"
      >
        <Input
          id="cfg-dc-field"
          value={fieldKey}
          onChange={(e) => onChange({ ...config, field_key: e.target.value })}
          placeholder="zip_code"
          className="h-8 text-sm font-mono"
          maxLength={64}
        />
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Min digits" htmlFor="cfg-dc-min">
          <Input
            id="cfg-dc-min"
            type="number"
            min={1}
            max={32}
            value={getNum(config, 'min_digits', 1)}
            onChange={(e) => onChange({ ...config, min_digits: Number(e.target.value) })}
            className="h-8 text-sm font-mono"
          />
        </Field>
        <Field label="Max digits" htmlFor="cfg-dc-max">
          <Input
            id="cfg-dc-max"
            type="number"
            min={1}
            max={32}
            value={getNum(config, 'max_digits', 16)}
            onChange={(e) => onChange({ ...config, max_digits: Number(e.target.value) })}
            className="h-8 text-sm font-mono"
          />
        </Field>
        <Field label="Max attempts" htmlFor="cfg-dc-attempts">
          <Input
            id="cfg-dc-attempts"
            type="number"
            min={1}
            max={10}
            value={getNum(config, 'max_attempts', 2)}
            onChange={(e) => onChange({ ...config, max_attempts: Number(e.target.value) })}
            className="h-8 text-sm font-mono"
          />
        </Field>
        <Field label="DTMF timeout (ms)" htmlFor="cfg-dc-timeout">
          <Input
            id="cfg-dc-timeout"
            type="number"
            min={500}
            max={60000}
            step={500}
            value={getNum(config, 'dtmf_timeout_ms', 6000)}
            onChange={(e) => onChange({ ...config, dtmf_timeout_ms: Number(e.target.value) })}
            className="h-8 text-sm font-mono"
          />
        </Field>
      </div>
      <Field label="Terminators" htmlFor="cfg-dc-term" hint="e.g. # to end digit collection">
        <Input
          id="cfg-dc-term"
          value={getStr(config, 'terminators', '#')}
          onChange={(e) => onChange({ ...config, terminators: e.target.value })}
          maxLength={8}
          className="h-8 text-sm font-mono w-28"
        />
      </Field>
    </div>
  );
}

export function PasswordNodeConfig({ config, onChange }: { config: Cfg; onChange: (c: Cfg) => void }) {
  const hash = getStr(config, 'password_hash');
  const plain = getStr(config, 'password');
  return (
    <div className="space-y-3">
      <Field
        label="bcrypt password hash"
        htmlFor="cfg-pwd-hash"
        hint="Strongly recommended. Generate with bcrypt (cost 10+)."
      >
        <Input
          id="cfg-pwd-hash"
          value={hash}
          onChange={(e) => onChange({ ...config, password_hash: e.target.value })}
          placeholder="$2b$10$…"
          className="h-8 text-sm font-mono"
        />
      </Field>
      <Field
        label="Plaintext password (dev only)"
        htmlFor="cfg-pwd-plain"
        hint="Warning: logged in plaintext. Use only for demos / low-sensitivity gates."
      >
        <Input
          id="cfg-pwd-plain"
          value={plain}
          onChange={(e) => onChange({ ...config, password: e.target.value })}
          placeholder="1234"
          className="h-8 text-sm font-mono"
        />
      </Field>
      <div className="grid grid-cols-3 gap-3">
        <Field label="Max attempts" htmlFor="cfg-pwd-attempts">
          <Input
            id="cfg-pwd-attempts"
            type="number"
            min={1}
            max={10}
            value={getNum(config, 'max_attempts', 3)}
            onChange={(e) => onChange({ ...config, max_attempts: Number(e.target.value) })}
            className="h-8 text-sm font-mono"
          />
        </Field>
        <Field label="Min digits" htmlFor="cfg-pwd-min">
          <Input
            id="cfg-pwd-min"
            type="number"
            min={1}
            max={32}
            value={getNum(config, 'min_digits', 4)}
            onChange={(e) => onChange({ ...config, min_digits: Number(e.target.value) })}
            className="h-8 text-sm font-mono"
          />
        </Field>
        <Field label="Max digits" htmlFor="cfg-pwd-max">
          <Input
            id="cfg-pwd-max"
            type="number"
            min={1}
            max={32}
            value={getNum(config, 'max_digits', 16)}
            onChange={(e) => onChange({ ...config, max_digits: Number(e.target.value) })}
            className="h-8 text-sm font-mono"
          />
        </Field>
      </div>
    </div>
  );
}

export function LabelRouteConfig({ config, onChange }: { config: Cfg; onChange: (c: Cfg) => void }) {
  const rules = Array.isArray(config.rules)
    ? (config.rules as Array<{ label_id?: string; edge?: string }>)
    : [];

  function updateRule(idx: number, patch: Partial<{ label_id: string; edge: string }>) {
    const next = rules.map((r, i) => (i === idx ? { ...r, ...patch } : r));
    onChange({ ...config, rules: next });
  }
  function addRule() {
    onChange({ ...config, rules: [...rules, { label_id: '', edge: '' }] });
  }
  function removeRule(idx: number) {
    onChange({ ...config, rules: rules.filter((_, i) => i !== idx) });
  }

  return (
    <div className="space-y-3">
      <p className="text-[11px] text-slate-500">
        First match wins. Each rule maps a label to an edge key — wire an
        outgoing edge with that key to route labeled callers. Unmatched callers
        take the <span className="font-mono">default</span> edge.
      </p>
      {rules.length === 0 && (
        <p className="text-xs italic text-slate-400">No rules yet — add one below.</p>
      )}
      {rules.map((rule, idx) => (
        <div key={idx} className="flex items-start gap-2 rounded-md border border-slate-100 bg-slate-50/50 p-2">
          <div className="grid flex-1 grid-cols-2 gap-2">
            <LabelSelectField
              value={rule.label_id ?? ''}
              onChange={(id) => updateRule(idx, { label_id: id })}
            />
            <Input
              value={rule.edge ?? ''}
              onChange={(e) => updateRule(idx, { edge: e.target.value })}
              placeholder="edge key (e.g. vip)"
              aria-label={`Rule ${idx + 1} edge`}
              className="h-8 text-xs font-mono"
              maxLength={64}
            />
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-8 px-2 text-red-500 hover:bg-red-50"
            onClick={() => removeRule(idx)}
            aria-label={`Remove rule ${idx + 1}`}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      ))}
      <Button type="button" variant="outline" size="sm" className="h-8 text-xs" onClick={addRule}>
        <Plus className="h-3 w-3 mr-1" /> Add rule
      </Button>
    </div>
  );
}

export function AddLabelConfig({ config, onChange }: { config: Cfg; onChange: (c: Cfg) => void }) {
  return (
    <Field label="Label" htmlFor="cfg-label-id" required>
      <LabelSelectField
        id="cfg-label-id"
        value={getStr(config, 'label_id')}
        onChange={(id) => onChange({ ...config, label_id: id })}
        required
      />
    </Field>
  );
}

export function RemoveLabelConfig(p: { config: Cfg; onChange: (c: Cfg) => void }) {
  return (
    <Field label="Label" htmlFor="cfg-rm-label-id" required>
      <LabelSelectField
        id="cfg-rm-label-id"
        value={getStr(p.config, 'label_id')}
        onChange={(id) => p.onChange({ ...p.config, label_id: id })}
        required
      />
    </Field>
  );
}

export function ModifyAuthConfig({ config, onChange }: { config: Cfg; onChange: (c: Cfg) => void }) {
  const action = getStr(config, 'action', 'clear_caller');
  return (
    <Field label="Action" htmlFor="cfg-mod-auth" hint="Currently only clear_caller is supported">
      <Select value={action} onValueChange={(v) => onChange({ ...config, action: v })}>
        <SelectTrigger id="cfg-mod-auth" className="h-8 text-sm">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="clear_caller" className="text-sm">Clear caller identity</SelectItem>
        </SelectContent>
      </Select>
    </Field>
  );
}

export function ModifyAudioConfig({ config, onChange }: { config: Cfg; onChange: (c: Cfg) => void }) {
  const autoBookmark = getBool(config, 'set_auto_bookmark', false);
  return (
    <Field label="Set auto-bookmark" hint="Programmatic preference change, no caller prompt">
      <div className="flex h-8 items-center gap-3">
        <Switch
          id="cfg-mod-audio-ab"
          checked={autoBookmark}
          onCheckedChange={(v) =>
            onChange({ ...config, set_auto_bookmark: v })
          }
          aria-label="Set auto-bookmark"
        />
        <span className="text-sm text-slate-600">
          {autoBookmark ? 'Enable auto-bookmark for this caller' : 'Disable auto-bookmark for this caller'}
        </span>
      </div>
    </Field>
  );
}

export function PlaybackSettingsConfig() {
  return (
    <div className="rounded-md bg-blue-50/60 border border-blue-100 px-3 py-2.5 text-xs text-blue-700">
      Interactive menu — caller toggles their own playback preferences via DTMF.
      Requires an authenticated caller (wire an auth gate before this node).
      No configuration required.
    </div>
  );
}

// ── Communication & actions ──────────────────────────────────────────────────

export function RecordConfig({ config, onChange }: { config: Cfg; onChange: (c: Cfg) => void }) {
  return (
    <div className="space-y-3">
      <Field
        label="Name template"
        htmlFor="cfg-rec-name"
        hint="Appears as the recording's name in the library"
      >
        <Input
          id="cfg-rec-name"
          value={getStr(config, 'name_template', '')}
          onChange={(e) => onChange({ ...config, name_template: e.target.value })}
          placeholder="Student testimonial"
          className="h-8 text-sm"
          maxLength={200}
        />
      </Field>
      <div className="grid grid-cols-3 gap-3">
        <Field label="Max seconds" htmlFor="cfg-rec-max" hint="1–600">
          <Input
            id="cfg-rec-max"
            type="number"
            min={1}
            max={600}
            value={getNum(config, 'max_seconds', 180)}
            onChange={(e) => onChange({ ...config, max_seconds: Number(e.target.value) })}
            className="h-8 text-sm font-mono"
          />
        </Field>
        <Field label="Silence threshold" htmlFor="cfg-rec-sil" hint="1–50">
          <Input
            id="cfg-rec-sil"
            type="number"
            min={1}
            max={50}
            value={getNum(config, 'silence_threshold', 20)}
            onChange={(e) => onChange({ ...config, silence_threshold: Number(e.target.value) })}
            className="h-8 text-sm font-mono"
          />
        </Field>
        <Field label="Silence hits" htmlFor="cfg-rec-hits">
          <Input
            id="cfg-rec-hits"
            type="number"
            min={1}
            max={30}
            value={getNum(config, 'silence_hits', 5)}
            onChange={(e) => onChange({ ...config, silence_hits: Number(e.target.value) })}
            className="h-8 text-sm font-mono"
          />
        </Field>
      </div>
      <Field label="Save to folder" htmlFor="cfg-rec-folder" hint="Optional — saves the recording directly into a folder">
        <FolderSelectField
          folderId={getStr(config, 'folder_id')}
          onChange={(id) => onChange({ ...config, folder_id: id || undefined })}
          optional
          id="cfg-rec-folder"
        />
      </Field>
    </div>
  );
}

export function VoicemailConfig({ config, onChange }: { config: Cfg; onChange: (c: Cfg) => void }) {
  const boxId = getStr(config, 'voicemail_box_id');
  const { data, isLoading } = useQuery({
    queryKey: ['voicemail-boxes', 'picker'],
    queryFn: () => voicemails.listBoxes({ limit: 200 }),
    staleTime: 60_000,
  });
  return (
    <div className="space-y-3">
      <Field label="Voicemail box" htmlFor="cfg-vm-box" required>
        <Select value={boxId} onValueChange={(v) => onChange({ ...config, voicemail_box_id: v })}>
          <SelectTrigger id="cfg-vm-box" className="h-8 text-sm">
            <SelectValue placeholder={isLoading ? 'Loading…' : 'Pick a voicemail box…'} />
          </SelectTrigger>
          <SelectContent>
            {(data?.rows ?? []).map((b) => (
              <SelectItem key={b.id} value={b.id} className="text-sm">
                <span className="flex items-center gap-2">
                  {b.name}
                  <span className="text-[10px] rounded border border-slate-200 bg-slate-50 px-1 py-0.5 text-slate-500">
                    {b.owner_kind}
                  </span>
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Field>
      <CreateVoicemailBoxInline
        onCreate={(id) => onChange({ ...config, voicemail_box_id: id })}
      />
      <Field label="Max message seconds" htmlFor="cfg-vm-max" hint="5–600, default 180">
        <Input
          id="cfg-vm-max"
          type="number"
          min={5}
          max={600}
          value={getNum(config, 'max_message_sec', 180)}
          onChange={(e) => onChange({ ...config, max_message_sec: Number(e.target.value) })}
          className="h-8 w-32 text-sm font-mono"
        />
      </Field>
      <Field
        label="Greeting prompt key"
        htmlFor="cfg-vm-greet"
        hint="Optional — falls back to voicemail.default_greeting"
      >
        <Input
          id="cfg-vm-greet"
          value={getStr(config, 'greeting_prompt_key', '')}
          onChange={(e) =>
            onChange({ ...config, greeting_prompt_key: e.target.value || '' })
          }
          placeholder="custom.greet"
          className="h-8 text-sm font-mono"
        />
      </Field>
    </div>
  );
}

export function ListenVoicemailConfig({ config, onChange }: { config: Cfg; onChange: (c: Cfg) => void }) {
  const boxId = getStr(config, 'voicemail_box_id');
  const { data, isLoading } = useQuery({
    queryKey: ['voicemail-boxes', 'picker'],
    queryFn: () => voicemails.listBoxes({ limit: 200 }),
    staleTime: 60_000,
  });
  return (
    <div className="space-y-1">
      <Field label="Voicemail box" htmlFor="cfg-lvm-box" required>
        <Select value={boxId} onValueChange={(v) => onChange({ ...config, voicemail_box_id: v })}>
          <SelectTrigger id="cfg-lvm-box" className="h-8 text-sm">
            <SelectValue placeholder={isLoading ? 'Loading…' : 'Pick a voicemail box…'} />
          </SelectTrigger>
          <SelectContent>
            {(data?.rows ?? []).map((b) => (
              <SelectItem key={b.id} value={b.id} className="text-sm">{b.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Field>
      <CreateVoicemailBoxInline
        onCreate={(id) => onChange({ ...config, voicemail_box_id: id })}
      />
    </div>
  );
}

export function ConferenceConfig({ config, onChange }: { config: Cfg; onChange: (c: Cfg) => void }) {
  const roomId = getStr(config, 'conference_room_id');
  const { data, isLoading } = useQuery({
    queryKey: ['conference-rooms', 'picker'],
    queryFn: () => conferenceRooms.list({ limit: 200 }),
    staleTime: 60_000,
  });
  return (
    <Field label="Conference room" htmlFor="cfg-conf-room" required>
      <Select value={roomId} onValueChange={(v) => onChange({ ...config, conference_room_id: v })}>
        <SelectTrigger id="cfg-conf-room" className="h-8 text-sm">
          <SelectValue placeholder={isLoading ? 'Loading…' : 'Pick a conference room…'} />
        </SelectTrigger>
        <SelectContent>
          {(data?.rows ?? []).map((r) => (
            <SelectItem key={r.id} value={r.id} className="text-sm">
              <span className="flex items-center gap-2">
                <span className="font-mono text-[11px] text-slate-500">{formatRef(r.ref_number, 'CONF')}</span>
                {r.name}
              </span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </Field>
  );
}

export function RingGroupConfig({ config, onChange }: { config: Cfg; onChange: (c: Cfg) => void }) {
  const id = getStr(config, 'ring_group_id');
  const { data, isLoading } = useQuery({
    queryKey: ['ring-groups', 'picker'],
    queryFn: () => ringGroups.list({ limit: 200 }),
    staleTime: 60_000,
  });
  return (
    <Field label="Ring group" htmlFor="cfg-rg" required>
      <Select value={id} onValueChange={(v) => onChange({ ...config, ring_group_id: v })}>
        <SelectTrigger id="cfg-rg" className="h-8 text-sm">
          <SelectValue placeholder={isLoading ? 'Loading…' : 'Pick a ring group…'} />
        </SelectTrigger>
        <SelectContent>
          {(data?.rows ?? []).map((g) => (
            <SelectItem key={g.id} value={g.id} className="text-sm">
              <span className="flex items-center gap-2">
                <span className="font-mono text-[11px] text-slate-500">{formatRef(g.ref_number, 'RG')}</span>
                {g.name}
                <span className="text-[10px] rounded border border-slate-200 bg-slate-50 px-1 py-0.5 text-slate-500">
                  {g.strategy}
                </span>
              </span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </Field>
  );
}

export function DialConfig({ config, onChange }: { config: Cfg; onChange: (c: Cfg) => void }) {
  const kind = getStr(config, 'target_kind', 'phone');
  const target = getStr(config, 'target');
  const timeoutSec = getNum(config, 'timeout_sec', 30);
  const targetHint =
    kind === 'phone' ? 'E.164 number, e.g. +18005551234'
    : kind === 'extension' ? 'Extension number, e.g. 1001'
    : 'Full SIP URI or dial-string, e.g. sip:user@host';
  return (
    <div className="space-y-3">
      <Field label="Target kind" htmlFor="cfg-dial-kind">
        <Select value={kind} onValueChange={(v) => onChange({ ...config, target_kind: v })}>
          <SelectTrigger id="cfg-dial-kind" className="h-8 text-sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="phone" className="text-sm">Phone number (carrier)</SelectItem>
            <SelectItem value="extension" className="text-sm">Local extension</SelectItem>
            <SelectItem value="sip" className="text-sm">SIP URI</SelectItem>
          </SelectContent>
        </Select>
      </Field>
      <Field label="Target" htmlFor="cfg-dial-target" required hint={targetHint}>
        <Input
          id="cfg-dial-target"
          value={target}
          onChange={(e) => onChange({ ...config, target: e.target.value })}
          className="h-8 text-sm font-mono"
          maxLength={256}
        />
      </Field>
      <Field label="Timeout (sec)" htmlFor="cfg-dial-timeout">
        <Input
          id="cfg-dial-timeout"
          type="number"
          min={1}
          max={600}
          value={timeoutSec}
          onChange={(e) => onChange({ ...config, timeout_sec: Number(e.target.value) })}
          className="h-8 w-28 text-sm font-mono"
        />
      </Field>
    </div>
  );
}

export function SipConnectConfig({ config, onChange }: { config: Cfg; onChange: (c: Cfg) => void }) {
  const uri = getStr(config, 'uri');
  return (
    <div className="space-y-3">
      <Field
        label="SIP URI"
        htmlFor="cfg-sip-uri"
        required
        hint="Must start with sip:, sips:, or sofia/"
      >
        <Input
          id="cfg-sip-uri"
          value={uri}
          onChange={(e) => onChange({ ...config, uri: e.target.value })}
          placeholder="sip:user@other-pbx.example.com"
          className="h-8 text-sm font-mono"
          maxLength={512}
        />
      </Field>
      <Field label="Timeout (sec)" htmlFor="cfg-sip-timeout">
        <Input
          id="cfg-sip-timeout"
          type="number"
          min={1}
          max={600}
          value={getNum(config, 'timeout_sec', 30)}
          onChange={(e) => onChange({ ...config, timeout_sec: Number(e.target.value) })}
          className="h-8 w-28 text-sm font-mono"
        />
      </Field>
    </div>
  );
}

export function ExtensionNodeConfig({ config, onChange }: { config: Cfg; onChange: (c: Cfg) => void }) {
  const extId = getStr(config, 'extension_id');
  const { data, isLoading } = useQuery({
    queryKey: ['extensions', 'picker'],
    queryFn: () => extensions.list({ limit: 500 }),
    staleTime: 60_000,
  });
  return (
    <div className="space-y-3">
      <Field label="Extension" htmlFor="cfg-ext" required>
        <Select value={extId} onValueChange={(v) => onChange({ ...config, extension_id: v })}>
          <SelectTrigger id="cfg-ext" className="h-8 text-sm">
            <SelectValue placeholder={isLoading ? 'Loading…' : 'Pick an extension…'} />
          </SelectTrigger>
          <SelectContent>
            {(data?.rows ?? []).map((e) => (
              <SelectItem key={e.id} value={e.id} className="text-sm">
                <span className="flex items-center gap-2">
                  <span className="font-mono text-sm">{e.number}</span>
                  {e.display_name && <span className="text-slate-600">{e.display_name}</span>}
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Field>
      <Field label="Timeout (sec)" htmlFor="cfg-ext-timeout">
        <Input
          id="cfg-ext-timeout"
          type="number"
          min={1}
          max={600}
          value={getNum(config, 'timeout_sec', 30)}
          onChange={(e) => onChange({ ...config, timeout_sec: Number(e.target.value) })}
          className="h-8 w-28 text-sm font-mono"
        />
      </Field>
    </div>
  );
}

export function NotifyBranchConfig({ config, onChange }: { config: Cfg; onChange: (c: Cfg) => void }) {
  return (
    <div className="space-y-3">
      <Field label="Notification kind" htmlFor="cfg-nb-kind">
        <Select
          value={getStr(config, 'kind', 'sms')}
          onValueChange={(v) => onChange({ ...config, kind: v })}
        >
          <SelectTrigger id="cfg-nb-kind" className="h-8 text-sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="sms" className="text-sm">SMS</SelectItem>
          </SelectContent>
        </Select>
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="To" htmlFor="cfg-nb-to" required hint="E.164 recipient number">
          <Input
            id="cfg-nb-to"
            value={getStr(config, 'to')}
            onChange={(e) => onChange({ ...config, to: e.target.value })}
            placeholder="+15551234567"
            className="h-8 text-sm font-mono"
          />
        </Field>
        <Field label="From" htmlFor="cfg-nb-from" hint="E.164 sender (optional)">
          <Input
            id="cfg-nb-from"
            value={getStr(config, 'from')}
            onChange={(e) => onChange({ ...config, from: e.target.value || '' })}
            placeholder="+15557654321"
            className="h-8 text-sm font-mono"
          />
        </Field>
      </div>
      <Field label="Message body" htmlFor="cfg-nb-body" required>
        <Textarea
          id="cfg-nb-body"
          value={getStr(config, 'body')}
          onChange={(e) => onChange({ ...config, body: e.target.value })}
          maxLength={1600}
          rows={3}
          placeholder="Caller reached escalation — please return their call"
          className="text-sm resize-none"
        />
        <p className="text-[10px] text-slate-400 text-right">
          {getStr(config, 'body').length}/1600
        </p>
      </Field>
    </div>
  );
}

export function PublicForumConfig({ config, onChange, nodeId, nodeRefNumber }: { config: Cfg; onChange: (c: Cfg) => void; nodeId?: string; nodeRefNumber?: number | null }) {
  // Public forum is a single self-contained menu node — the legacy
  // mode toggle was removed May 2026. New nodes never set `mode`;
  // existing trees with `mode: 'post' | 'listen'` keep working
  // because the engine still reads the field for back-compat.
  //
  // moderation defaults ON (true = manual). This matches the engine
  // default in publicForum.ts → cfg.moderation === 'auto' ? 'auto' : 'manual'.
  const requireModeration = getBool(config, 'moderation', true) !== false
    && getStr(config, 'moderation', 'manual') !== 'auto';
  const folderId = getStr(config, 'folder_id');

  return (
    <div className="space-y-3">
      <div className="rounded-md border border-indigo-100 bg-indigo-50/60 px-3 py-2 text-[11px] text-slate-600 leading-relaxed">
        <p className="font-medium text-slate-700 mb-0.5">Public forum (menu)</p>
        <p>
          This node plays a built-in menu: <span className="font-mono">1</span> to
          record, <span className="font-mono">2</span> to listen. Upload a
          <span className="font-mono"> main </span>recording to replace the menu
          prompt;<span className="font-mono"> intro1</span> /
          <span className="font-mono"> intro2 </span>play before it, and
          <span className="font-mono"> outro </span>plays after the chosen
          sub-flow completes.
        </p>
      </div>

      {nodeId && (
        <a
          // Prefer the human-readable ref form (IVR-N) when available.
          // Backend's /forum-messages endpoint resolves either UUID or
          // ref to the underlying node_id (Sam 2026-05-21).
          href={`/forum?node=${encodeURIComponent(
            nodeRefNumber != null ? `IVR-${nodeRefNumber}` : nodeId,
          )}`}
          className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 transition-colors"
        >
          View this forum&rsquo;s messages →
        </a>
      )}

      <Field label="Require moderation" hint="When off, posts go live immediately (auto-publish).">
        <div className="flex h-8 items-center gap-3">
          <Switch
            id="cfg-pf-mod"
            checked={requireModeration}
            onCheckedChange={(v) =>
              onChange({ ...config, moderation: v ? 'manual' : 'auto' })
            }
            aria-label="Require moderation"
          />
          <span className="text-sm text-slate-600">
            {requireModeration
              ? 'Hold each post for staff approval'
              : 'Publish posts immediately (no review)'}
          </span>
        </div>
      </Field>

      <Field
        label="Save approved posts to folder"
        hint="Optional. The folder shows up as a normal bulletin in other IVR nodes. Pending posts stay hidden until approved."
        htmlFor="cfg-pf-folder"
      >
        <FolderSelectField
          folderId={folderId}
          onChange={(id) => onChange({ ...config, folder_id: id })}
          optional
          id="cfg-pf-folder"
        />
      </Field>

      <div className="grid grid-cols-2 gap-3">
        <Field label="Max seconds (post)" htmlFor="cfg-pf-max">
          <Input
            id="cfg-pf-max"
            type="number"
            min={1}
            max={600}
            value={getNum(config, 'max_seconds', 180)}
            onChange={(e) => onChange({ ...config, max_seconds: Number(e.target.value) })}
            className="h-8 text-sm font-mono"
          />
        </Field>
      </div>
      {/* Listen plays every approved message (newest first) per Sam
          2026-05-19 — no per-node cap. The engine internally limits to
          500 to keep query / playback bounded; in practice that's
          effectively "all". */}
    </div>
  );
}

// ── Payments ─────────────────────────────────────────────────────────────────

// ── PaymentConfig sub-editors (tiered amounts, decline routing) ─────────

interface TierRow { key: string; amount_cents: number; label?: string }

function TieredAmountsEditor({
  value, onChange,
}: { value: TierRow[]; onChange: (next: TierRow[]) => void }) {
  const rows: TierRow[] = Array.isArray(value) ? value : [];
  const addRow = () => {
    if (rows.length >= 9) return;     // DTMF 1–9
    const usedKeys = new Set(rows.map((r) => r.key));
    let nextKey = '1';
    for (let i = 1; i <= 9; i++) {
      if (!usedKeys.has(String(i))) { nextKey = String(i); break; }
    }
    onChange([...rows, { key: nextKey, amount_cents: 2500 }]);
  };
  return (
    <div className="space-y-2">
      <div className="space-y-1">
        {rows.map((r, idx) => (
          <div key={idx} className="flex items-center gap-2">
            <Input
              value={r.key}
              maxLength={1}
              onChange={(e) => {
                const k = e.target.value.replace(/[^1-9]/g, '').slice(0, 1);
                const next = [...rows];
                next[idx] = { ...r, key: k };
                onChange(next);
              }}
              className="h-7 w-12 text-sm font-mono text-center"
            />
            <span className="text-xs text-slate-400">→ $</span>
            <Input
              type="number" min={0.01} step={0.01}
              value={(r.amount_cents / 100).toFixed(2)}
              onChange={(e) => {
                const cents = Math.round(Number(e.target.value) * 100);
                const next = [...rows];
                next[idx] = { ...r, amount_cents: Number.isFinite(cents) ? cents : 0 };
                onChange(next);
              }}
              className="h-7 w-24 text-sm font-mono"
            />
            <Input
              value={r.label ?? ''}
              onChange={(e) => {
                const next = [...rows];
                next[idx] = { ...r, label: e.target.value };
                onChange(next);
              }}
              placeholder="label (optional)"
              maxLength={40}
              className="h-7 text-sm flex-1"
            />
            <button
              type="button"
              onClick={() => onChange(rows.filter((_, i) => i !== idx))}
              className="h-7 w-7 inline-flex items-center justify-center rounded text-slate-400 hover:text-red-600 hover:bg-red-50"
              aria-label={`Remove tier ${r.key}`}
            >
              ×
            </button>
          </div>
        ))}
        {rows.length === 0 && (
          <p className="text-[11px] text-slate-400 italic">
            No tiers yet. Add at least one to enable the tiered menu.
          </p>
        )}
      </div>
      <button
        type="button"
        onClick={addRow}
        disabled={rows.length >= 9}
        className="inline-flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed"
      >
        + Add tier {rows.length >= 9 ? '(max 9)' : ''}
      </button>
    </div>
  );
}

const DECLINE_ERROR_CODES = [
  'card_declined', 'insufficient_funds', 'expired_card', 'invalid_card',
  'fraud_suspected', 'processing_error', 'gateway_unavailable',
  'config_error', 'unknown',
] as const;

/**
 * TreeNodeCombobox — searchable picker over the surrounding IVR tree's
 * nodes (rule 13: no raw UUID inputs). Options are labelled
 * "IVR-ref · name" with the node-type badge; search matches ref, name,
 * and type. Stores/emits the node UUID, so consumers' config shape is
 * unchanged. Follows the TimezonePicker Popover+Command pattern.
 */
function TreeNodeCombobox({
  value, onChange, treeNodes, excludeNodeId, placeholder = 'Pick a node…',
}: {
  /** Selected node UUID, or '' for none. */
  value: string;
  onChange: (nodeId: string) => void;
  treeNodes: IvrNodeRow[];
  /** Usually the node being edited — routing to self is never useful. */
  excludeNodeId?: string;
  placeholder?: string;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const options = treeNodes.filter((n) => n.id !== excludeNodeId);
  const selected = options.find((n) => n.id === value) ?? null;
  // A UUID that no longer resolves within this tree (node deleted, or a
  // legacy value pasted before the picker existed). Surface it instead
  // of silently showing the placeholder — the caller can clear it.
  const isUnknownValue = !!value && !selected;

  const q = search.trim().toLowerCase();
  const filtered = q
    ? options.filter((n) =>
        n.name.toLowerCase().includes(q)
        || formatRef(n.ref_number, 'IVR').toLowerCase().includes(q)
        || n.node_type.toLowerCase().includes(q))
    : options;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="h-7 w-full justify-between px-2 text-sm font-normal"
        >
          <span className="flex min-w-0 items-center gap-1.5 truncate">
            {selected ? (
              <>
                <span className="shrink-0 font-mono text-[11px] text-slate-500">
                  {formatRef(selected.ref_number, 'IVR')}
                </span>
                <span className="truncate">{selected.name}</span>
              </>
            ) : isUnknownValue ? (
              <span className="truncate font-mono text-[11px] text-amber-600" title={value}>
                Unknown node ({value.slice(0, 8)}…)
              </span>
            ) : (
              <span className="truncate text-slate-400">{placeholder}</span>
            )}
          </span>
          <ChevronsUpDown className="ms-1.5 h-3.5 w-3.5 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[320px] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Search by name or IVR ref…"
            value={search}
            onValueChange={setSearch}
          />
          <CommandList className="max-h-56">
            {filtered.length === 0 && (
              <CommandEmpty>
                {options.length === 0 ? 'No other nodes in this tree yet.' : 'No matching node.'}
              </CommandEmpty>
            )}
            {filtered.map((n) => (
              <CommandItem
                key={n.id}
                value={n.id}
                onSelect={() => {
                  onChange(n.id);
                  setOpen(false);
                  setSearch('');
                }}
              >
                <Check
                  className={cn('me-2 h-3.5 w-3.5 shrink-0', value === n.id ? 'opacity-100' : 'opacity-0')}
                />
                <span className="flex min-w-0 items-center gap-1.5">
                  <span className="shrink-0 font-mono text-[11px] text-slate-500">
                    {formatRef(n.ref_number, 'IVR')}
                  </span>
                  <span className="truncate">{n.name}</span>
                  <NodeTypeBadge type={n.node_type} />
                </span>
              </CommandItem>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function DeclineRoutingEditor({
  value, onChange, treeNodes, currentNodeId,
}: {
  value: Record<string, string>;
  onChange: (next: Record<string, string>) => void;
  /** All nodes in the surrounding tree — threaded from NodeEditorDrawer
   *  via TypedConfigForm → PaymentConfig (rule 13). */
  treeNodes: IvrNodeRow[];
  /** The payment node being edited; excluded from the picker. */
  currentNodeId?: string;
}) {
  const setCode = (code: string, nodeId: string) => {
    const next = { ...value };
    if (nodeId) next[code] = nodeId;
    else delete next[code];
    onChange(next);
  };
  return (
    <div className="space-y-1.5">
      {DECLINE_ERROR_CODES.map((code) => (
        <div key={code} className="flex items-center gap-2">
          <code className="w-36 shrink-0 rounded bg-slate-100 px-1.5 py-0.5 text-[11px] text-slate-600">
            {code}
          </code>
          <span className="text-xs text-slate-400" aria-hidden>→</span>
          <div className="min-w-0 flex-1">
            <TreeNodeCombobox
              value={value[code] ?? ''}
              onChange={(nodeId) => setCode(code, nodeId)}
              treeNodes={treeNodes}
              excludeNodeId={currentNodeId}
              placeholder="Default declined edge"
            />
          </div>
          {value[code] && (
            <button
              type="button"
              onClick={() => setCode(code, '')}
              className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded text-slate-400 hover:bg-red-50 hover:text-red-600"
              aria-label={`Clear ${code} routing (use default declined edge)`}
              title="Clear — fall back to the default declined edge"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      ))}
    </div>
  );
}

export function PaymentConfig({
  config, onChange, treeNodes = [], currentNodeId,
}: {
  config: Cfg;
  onChange: (c: Cfg) => void;
  /** Nodes of the surrounding tree, for the decline-routing picker
   *  (rule 13). NodeEditorDrawer always supplies them. */
  treeNodes?: IvrNodeRow[];
  /** UUID of the payment node being edited — excluded from the picker. */
  currentNodeId?: string;
}) {
  const amount = getNum(config, 'amount_cents', 0) ?? 0;
  const amountDollars = (amount / 100).toFixed(2);
  const isRecurring = getBool(config, 'is_recurring', false);
  const callerChoosesMonths = getBool(config, 'caller_chooses_months', false);
  const recurringMonths = getNum(config, 'recurring_months', 12) ?? 12;
  const { data, isLoading } = useQuery({
    queryKey: ['payment-gateways', 'picker'],
    queryFn: () => paymentGateways.list({ limit: 200 }),
    staleTime: 60_000,
  });
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <Field label="Amount (dollars)" htmlFor="cfg-pay-amount" required hint="Charged to the caller">
          <Input
            id="cfg-pay-amount"
            type="number"
            min={0.01}
            step={0.01}
            value={amountDollars}
            onChange={(e) => {
              const cents = Math.round(Number(e.target.value) * 100);
              onChange({ ...config, amount_cents: Number.isFinite(cents) ? cents : 0 });
            }}
            className="h-8 text-sm font-mono"
          />
        </Field>
        <Field label="Currency" htmlFor="cfg-pay-currency" hint="ISO 4217, default USD">
          <Input
            id="cfg-pay-currency"
            value={getStr(config, 'currency', 'USD')}
            onChange={(e) => onChange({ ...config, currency: e.target.value.toUpperCase() })}
            maxLength={3}
            className="h-8 text-sm font-mono uppercase"
          />
        </Field>
      </div>
      <Field
        label="Payment gateway"
        htmlFor="cfg-pay-gw"
        hint="Leave empty to use your account default"
      >
        <Select
          value={getStr(config, 'gateway_id') || '__default__'}
          onValueChange={(v) =>
            onChange({ ...config, gateway_id: v === '__default__' ? '' : v })
          }
        >
          <SelectTrigger id="cfg-pay-gw" className="h-8 text-sm">
            <SelectValue placeholder={isLoading ? 'Loading…' : 'Account default'} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__default__" className="text-sm">
              <span className="italic text-slate-500">Account default</span>
            </SelectItem>
            {(data?.rows ?? []).map((g) => (
              <SelectItem key={g.id} value={g.id} className="text-sm">
                <span className="flex items-center gap-2">
                  {g.name}
                  <span className="text-[10px] rounded border border-slate-200 bg-slate-50 px-1 py-0.5 text-slate-500 uppercase">
                    {g.kind}
                  </span>
                  {g.is_account_default && (
                    <span className="text-[10px] rounded bg-emerald-50 border border-emerald-200 px-1 py-0.5 text-emerald-700">
                      default
                    </span>
                  )}
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Field>
      <Field label="Description" htmlFor="cfg-pay-desc" hint="Passed to the gateway as metadata">
        <Input
          id="cfg-pay-desc"
          value={getStr(config, 'description')}
          onChange={(e) => onChange({ ...config, description: e.target.value })}
          placeholder="Course fee"
          className="h-8 text-sm"
          maxLength={500}
        />
      </Field>

      {/* Recurring charge */}
      <div className="flex items-center gap-3">
        <Switch
          id="cfg-pay-recurring"
          checked={isRecurring}
          onCheckedChange={(v) => onChange({ ...config, is_recurring: v })}
        />
        <Label htmlFor="cfg-pay-recurring" className="text-xs cursor-pointer">
          Recurring charge
        </Label>
      </div>

      {isRecurring && (
        <div className="rounded-md border border-slate-100 bg-slate-50/50 p-3 space-y-3">
          <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">Duration</p>
          <div className="space-y-2">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="cfg-pay-duration-mode"
                checked={!callerChoosesMonths}
                onChange={() => onChange({ ...config, caller_chooses_months: false, recurring_months: recurringMonths })}
                className="accent-indigo-600"
              />
              <span className="text-xs text-slate-700 flex items-center gap-2">
                Fixed —
                <Input
                  type="number"
                  min={1}
                  max={120}
                  value={recurringMonths}
                  disabled={callerChoosesMonths}
                  onChange={(e) => onChange({ ...config, recurring_months: Number(e.target.value), caller_chooses_months: false })}
                  onClick={(e) => { e.stopPropagation(); onChange({ ...config, caller_chooses_months: false }); }}
                  className="h-7 w-20 text-sm font-mono"
                />
                months
              </span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="cfg-pay-duration-mode"
                checked={callerChoosesMonths}
                onChange={() => onChange({ ...config, caller_chooses_months: true, recurring_months: null })}
                className="accent-indigo-600"
              />
              <span className="text-xs text-slate-700">Caller chooses on call</span>
            </label>
          </div>
          <p className="text-[11px] text-slate-400">
            Fixed: charge for a set number of months. Caller chooses: the IVR prompts the caller to enter the duration.
          </p>
        </div>
      )}

      {/* ── 0070 additions: mode toggle + category + receipts + notify ─── */}

      <Field label="Mode" htmlFor="cfg-pay-mode" hint="Test mode hits the gateway sandbox; live uses your live keys.">
        <Select
          value={getStr(config, 'mode', 'live') || 'live'}
          onValueChange={(v) => onChange({ ...config, mode: v })}
        >
          <SelectTrigger id="cfg-pay-mode" className="h-8 text-sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="live" className="text-sm">Live</SelectItem>
            <SelectItem value="test" className="text-sm">Test (sandbox)</SelectItem>
          </SelectContent>
        </Select>
      </Field>

      <Field label="Category / designation" htmlFor="cfg-pay-category" hint="Tags every charge from this node. Pick from your account's category list (Payments → Payment settings).">
        <Input
          id="cfg-pay-category"
          value={getStr(config, 'category')}
          onChange={(e) => onChange({ ...config, category: e.target.value })}
          placeholder="general"
          className="h-8 text-sm"
          maxLength={80}
        />
      </Field>

      <div className="rounded-md border border-slate-100 bg-slate-50/50 p-3 space-y-3">
        <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">
          Receipts to caller
        </p>
        <div className="flex items-center gap-3">
          <Switch
            id="cfg-pay-receipt-sms"
            checked={getBool(config, 'receipt_sms', false)}
            onCheckedChange={(v) => onChange({ ...config, receipt_sms: v })}
          />
          <Label htmlFor="cfg-pay-receipt-sms" className="text-xs cursor-pointer">
            Send SMS receipt (requires caller_profile phone)
          </Label>
        </div>
        <div className="flex items-center gap-3">
          <Switch
            id="cfg-pay-receipt-email"
            checked={getBool(config, 'receipt_email', false)}
            onCheckedChange={(v) => onChange({ ...config, receipt_email: v })}
          />
          <Label htmlFor="cfg-pay-receipt-email" className="text-xs cursor-pointer">
            Send email receipt (requires caller_profile email)
          </Label>
        </div>
        <div className="flex items-center gap-3">
          <Switch
            id="cfg-pay-receipt-voice"
            checked={getBool(config, 'receipt_voice_readback', false)}
            onCheckedChange={(v) => onChange({ ...config, receipt_voice_readback: v })}
          />
          <Label htmlFor="cfg-pay-receipt-voice" className="text-xs cursor-pointer">
            Speak the confirmation # back on the call
          </Label>
        </div>
      </div>

      <div className="rounded-md border border-slate-100 bg-slate-50/50 p-3 space-y-3">
        <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">
          Customer notification
        </p>
        <div className="flex items-center gap-3">
          <Switch
            id="cfg-pay-notify"
            checked={getBool(config, 'notify_customer_on_success', false)}
            onCheckedChange={(v) => onChange({ ...config, notify_customer_on_success: v })}
          />
          <Label htmlFor="cfg-pay-notify" className="text-xs cursor-pointer">
            Email + SMS the org admin on every successful charge
          </Label>
        </div>
        {getBool(config, 'notify_customer_on_success', false) && (
          <div className="flex items-center gap-3 ms-7">
            <Switch
              id="cfg-pay-notify-recording"
              checked={getBool(config, 'notify_attach_recording', false)}
              onCheckedChange={(v) => onChange({ ...config, notify_attach_recording: v })}
            />
            <Label htmlFor="cfg-pay-notify-recording" className="text-xs cursor-pointer">
              Include a link to the caller's recording (when present)
            </Label>
          </div>
        )}
        <p className="text-[11px] text-slate-400">
          Sends to <code>customers.contact_email</code> and the customer's
          first active SMS-enabled DID. Configurable per node so a
          high-volume node can opt out without losing notifications on
          others.
        </p>
      </div>

      {/* ── 0071 additions: amount mode + tiered + card extras + recordings + retries + routing ── */}

      <div className="rounded-md border border-slate-100 bg-slate-50/50 p-3 space-y-3">
        <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">
          Amount mode
        </p>
        <Select
          value={getStr(config, 'amount_mode', 'static') || 'static'}
          onValueChange={(v) => onChange({ ...config, amount_mode: v })}
        >
          <SelectTrigger className="h-8 text-sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="static" className="text-sm">Fixed (single amount)</SelectItem>
            <SelectItem value="caller_choose" className="text-sm">Caller chooses (within min/max)</SelectItem>
            <SelectItem value="tiered" className="text-sm">Tiered menu (press 1 for X, 2 for Y…)</SelectItem>
          </SelectContent>
        </Select>

        {(getStr(config, 'amount_mode') || 'static') === 'tiered' && (
          <div className="space-y-2">
            <p className="text-[11px] text-slate-400">
              Caller presses a key to pick a tier. Add up to 9 tiers; the
              key field is the DTMF digit (1–9) the caller presses.
            </p>
            <TieredAmountsEditor
              value={
                Array.isArray((config as { tiered_amounts?: unknown }).tiered_amounts)
                  ? (config as { tiered_amounts: Array<{ key: string; amount_cents: number; label?: string }> }).tiered_amounts
                  : []
              }
              onChange={(next) => onChange({ ...config, tiered_amounts: next })}
            />
          </div>
        )}

        {(getStr(config, 'amount_mode') || 'static') === 'caller_choose' && (
          <div className="grid grid-cols-2 gap-2">
            <Field label="Min ($)" htmlFor="cfg-pay-min" hint="Floor for caller-entered amount">
              <Input
                id="cfg-pay-min"
                type="number" min={0.01} step={0.01}
                value={((getNum(config, 'amount_min_cents', 100) ?? 100) / 100).toFixed(2)}
                onChange={(e) => onChange({
                  ...config,
                  amount_min_cents: Math.round(Number(e.target.value) * 100),
                })}
                className="h-8 text-sm font-mono"
              />
            </Field>
            <Field label="Max ($)" htmlFor="cfg-pay-max" hint="Ceiling for caller-entered amount">
              <Input
                id="cfg-pay-max"
                type="number" min={0.01} step={0.01}
                value={((getNum(config, 'amount_max_cents', 1_000_000) ?? 1_000_000) / 100).toFixed(2)}
                onChange={(e) => onChange({
                  ...config,
                  amount_max_cents: Math.round(Number(e.target.value) * 100),
                })}
                className="h-8 text-sm font-mono"
              />
            </Field>
          </div>
        )}
      </div>

      <div className="rounded-md border border-slate-100 bg-slate-50/50 p-3 space-y-3">
        <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">
          Card capture
        </p>
        <div className="flex items-center gap-3">
          <Switch
            id="cfg-pay-zip"
            checked={getBool(config, 'collect_zip', false)}
            onCheckedChange={(v) => onChange({ ...config, collect_zip: v })}
          />
          <Label htmlFor="cfg-pay-zip" className="text-xs cursor-pointer">
            Collect billing zip (AVS) — extra fraud protection
          </Label>
        </div>
        <div className="flex items-center gap-3">
          <Switch
            id="cfg-pay-pause"
            checked={getBool(config, 'pause_recording_during_card', true)}
            onCheckedChange={(v) => onChange({ ...config, pause_recording_during_card: v })}
          />
          <Label htmlFor="cfg-pay-pause" className="text-xs cursor-pointer">
            Pause call recording during card entry (PCI; default on)
          </Label>
        </div>
        <div className="flex items-center gap-3">
          <Switch
            id="cfg-pay-saved-card"
            checked={getBool(config, 'offer_saved_card', false)}
            onCheckedChange={(v) => onChange({ ...config, offer_saved_card: v })}
          />
          <Label htmlFor="cfg-pay-saved-card" className="text-xs cursor-pointer">
            Offer to reuse saved card (when caller has one on file)
          </Label>
        </div>
      </div>

      <div className="rounded-md border border-slate-100 bg-slate-50/50 p-3 space-y-3">
        <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">
          Caller recordings
        </p>
        <div className="flex items-center gap-3">
          <Switch
            id="cfg-pay-pre-rec"
            checked={getBool(config, 'pre_recording_enabled', false)}
            onCheckedChange={(v) => onChange({ ...config, pre_recording_enabled: v })}
          />
          <Label htmlFor="cfg-pay-pre-rec" className="text-xs cursor-pointer">
            Ask caller to leave an intro message before charging
          </Label>
        </div>
        {getBool(config, 'pre_recording_enabled', false) && (
          <Field label="Max length (seconds)" htmlFor="cfg-pay-pre-max">
            <Input
              id="cfg-pay-pre-max" type="number" min={5} max={120}
              value={getNum(config, 'pre_recording_max_seconds', 30) ?? 30}
              onChange={(e) => onChange({ ...config, pre_recording_max_seconds: Number(e.target.value) })}
              className="h-8 w-24 text-sm font-mono"
            />
          </Field>
        )}
        <div className="flex items-center gap-3">
          <Switch
            id="cfg-pay-post-rec"
            checked={getBool(config, 'post_recording_enabled', false)}
            onCheckedChange={(v) => onChange({ ...config, post_recording_enabled: v })}
          />
          <Label htmlFor="cfg-pay-post-rec" className="text-xs cursor-pointer">
            Offer dedication recording after success (honor / memorial)
          </Label>
        </div>
        {getBool(config, 'post_recording_enabled', false) && (
          <div className="grid grid-cols-2 gap-2">
            <Field label="Max length (seconds)" htmlFor="cfg-pay-post-max">
              <Input
                id="cfg-pay-post-max" type="number" min={5} max={120}
                value={getNum(config, 'post_recording_max_seconds', 30) ?? 30}
                onChange={(e) => onChange({ ...config, post_recording_max_seconds: Number(e.target.value) })}
                className="h-8 text-sm font-mono"
              />
            </Field>
            <Field label="Threshold ($)" htmlFor="cfg-pay-post-thresh" hint="Only offer when total ≥ this. 0 = always.">
              <Input
                id="cfg-pay-post-thresh" type="number" min={0} step={1}
                value={((getNum(config, 'post_recording_min_amount_cents', 0) ?? 0) / 100).toFixed(0)}
                onChange={(e) => onChange({
                  ...config,
                  post_recording_min_amount_cents: Math.round(Number(e.target.value) * 100),
                })}
                className="h-8 text-sm font-mono"
              />
            </Field>
          </div>
        )}
      </div>

      <div className="rounded-md border border-slate-100 bg-slate-50/50 p-3 space-y-3">
        <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">
          Retry on temporary decline
        </p>
        <Field label="Max retry attempts" htmlFor="cfg-pay-retry" hint="Up to 5. Each retry creates its own ledger row.">
          <Input
            id="cfg-pay-retry" type="number" min={0} max={5}
            value={getNum(config, 'retry_max', 0) ?? 0}
            onChange={(e) => onChange({ ...config, retry_max: Number(e.target.value) })}
            className="h-8 w-24 text-sm font-mono"
          />
        </Field>
        <p className="text-[11px] text-slate-400">
          Retries fire on <code>insufficient_funds</code> +
          <code> processing_error</code> by default. Permanent declines
          (expired card / invalid card / fraud) never retry.
        </p>
      </div>

      <div className="rounded-md border border-slate-100 bg-slate-50/50 p-3 space-y-3">
        <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">
          Decline-reason routing (advanced)
        </p>
        <p className="text-[11px] text-slate-400">
          When set, the engine jumps to the picked node instead of the
          generic <code>declined</code> edge for that decline reason.
          Leave a row empty to fall through to the default.
        </p>
        <DeclineRoutingEditor
          value={(config as { routing_by_error?: Record<string, string> }).routing_by_error ?? {}}
          onChange={(next) => onChange({ ...config, routing_by_error: next })}
          treeNodes={treeNodes}
          currentNodeId={currentNodeId}
        />
      </div>
    </div>
  );
}

export function ShoppingConfig({ config, onChange }: { config: Cfg; onChange: (c: Cfg) => void }) {
  return (
    <div className="space-y-3">
      <div className="rounded-md bg-amber-50 border border-amber-200 px-3 py-2.5 text-xs text-amber-800">
        Placeholder — the shopping module (products + orders) is not yet
        implemented. The node currently routes all callers to the
        <span className="font-mono mx-1">unavailable</span> edge. Wire that
        edge to a graceful "not available" prompt.
      </div>
      <Field label="Product ID (optional)" htmlFor="cfg-shop-id">
        <Input
          id="cfg-shop-id"
          value={getStr(config, 'product_id')}
          onChange={(e) => onChange({ ...config, product_id: e.target.value })}
          placeholder="future product uuid or sku"
          className="h-8 text-sm font-mono"
          maxLength={200}
        />
      </Field>
    </div>
  );
}

// ── Scheduling ───────────────────────────────────────────────────────────────

const TZ_SUGGESTIONS = [
  'UTC',
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'Europe/London',
  'Europe/Paris',
  'Asia/Jerusalem',
  'Australia/Sydney',
];

function TimezoneField({
  value,
  onChange,
  id,
}: {
  value: string;
  onChange: (v: string) => void;
  id: string;
}) {
  return (
    <Field label="Timezone" htmlFor={id} hint="IANA tz (defaults to UTC if invalid)">
      <Input
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="America/New_York"
        list={`${id}-list`}
        className="h-8 text-sm font-mono"
      />
      <datalist id={`${id}-list`}>
        {TZ_SUGGESTIONS.map((tz) => <option key={tz} value={tz} />)}
      </datalist>
    </Field>
  );
}

const DAY_KEYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'] as const;
type DayKey = typeof DAY_KEYS[number];

function DaysPicker({
  value,
  onChange,
}: {
  value: DayKey[] | undefined;
  onChange: (v: DayKey[] | undefined) => void;
}) {
  const set = new Set(value ?? []);
  function toggle(d: DayKey) {
    if (set.has(d)) set.delete(d);
    else set.add(d);
    const next = DAY_KEYS.filter((x) => set.has(x));
    onChange(next.length ? next : undefined);
  }
  return (
    <div className="flex flex-wrap gap-1">
      {DAY_KEYS.map((d) => {
        const on = set.has(d);
        return (
          <button
            type="button"
            key={d}
            onClick={() => toggle(d)}
            className={
              'rounded border px-2 py-1 text-[11px] font-mono uppercase transition-colors ' +
              (on
                ? 'border-indigo-300 bg-indigo-50 text-indigo-700'
                : 'border-slate-200 bg-white text-slate-500 hover:bg-slate-50')
            }
          >
            {d}
          </button>
        );
      })}
    </div>
  );
}

export function DayOfWeekConfig({ config, onChange }: { config: Cfg; onChange: (c: Cfg) => void }) {
  return (
    <div className="space-y-3">
      <TimezoneField
        id="cfg-dow-tz"
        value={getStr(config, 'timezone', '')}
        onChange={(v) => onChange({ ...config, timezone: v || '' })}
      />
      <p className="text-[11px] text-slate-500">
        Edges: one per day (<span className="font-mono">sun…sat</span>), plus
        umbrella <span className="font-mono">weekday</span> /
        <span className="font-mono">weekend</span>, plus
        <span className="font-mono">default</span>.
      </p>
    </div>
  );
}

export function TimerConfig({ config, onChange }: { config: Cfg; onChange: (c: Cfg) => void }) {
  const days = Array.isArray(config.days)
    ? (config.days as DayKey[]).filter((d) => DAY_KEYS.includes(d))
    : undefined;
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <Field label="Start time" htmlFor="cfg-timer-start" required hint="24-hour HH:MM">
          <Input
            id="cfg-timer-start"
            value={getStr(config, 'start')}
            onChange={(e) => onChange({ ...config, start: e.target.value })}
            placeholder="09:00"
            className="h-8 text-sm font-mono"
          />
        </Field>
        <Field label="End time" htmlFor="cfg-timer-end" required hint="Exclusive; overnight windows supported">
          <Input
            id="cfg-timer-end"
            value={getStr(config, 'end')}
            onChange={(e) => onChange({ ...config, end: e.target.value })}
            placeholder="17:00"
            className="h-8 text-sm font-mono"
          />
        </Field>
      </div>
      <TimezoneField
        id="cfg-timer-tz"
        value={getStr(config, 'timezone', '')}
        onChange={(v) => onChange({ ...config, timezone: v || '' })}
      />
      <Field label="Days of week" hint="Leave empty to match all 7 days">
        <DaysPicker
          value={days}
          onChange={(v) => onChange({ ...config, days: v })}
        />
      </Field>
    </div>
  );
}

export function SwitchTimerConfig({ config, onChange }: { config: Cfg; onChange: (c: Cfg) => void }) {
  const windows = Array.isArray(config.windows)
    ? (config.windows as Array<{ name?: string; start?: string; end?: string; days?: DayKey[] }>)
    : [];

  function updateWindow(idx: number, patch: Partial<{ name: string; start: string; end: string; days: DayKey[] | undefined }>) {
    const next = windows.map((w, i) => (i === idx ? { ...w, ...patch } : w));
    onChange({ ...config, windows: next });
  }
  function addWindow() {
    onChange({
      ...config,
      windows: [...windows, { name: '', start: '09:00', end: '17:00' }],
    });
  }
  function removeWindow(idx: number) {
    onChange({ ...config, windows: windows.filter((_, i) => i !== idx) });
  }

  return (
    <div className="space-y-3">
      <TimezoneField
        id="cfg-sw-tz"
        value={getStr(config, 'timezone', '')}
        onChange={(v) => onChange({ ...config, timezone: v || '' })}
      />
      <p className="text-[11px] text-slate-500">
        First matching window wins. Wire one edge per window name, plus
        <span className="font-mono mx-1">outside</span> fallback.
      </p>
      {windows.length === 0 && (
        <p className="text-xs italic text-slate-400">No windows yet — add one below.</p>
      )}
      {windows.map((w, idx) => (
        <div
          key={idx}
          className="space-y-2 rounded-md border border-slate-100 bg-slate-50/50 p-3"
        >
          <div className="flex items-start gap-2">
            <div className="flex-1 space-y-2">
              <Input
                value={w.name ?? ''}
                onChange={(e) => updateWindow(idx, { name: e.target.value })}
                placeholder="Window name (e.g. morning)"
                aria-label={`Window ${idx + 1} name`}
                className="h-8 text-sm"
                maxLength={64}
              />
              <div className="grid grid-cols-2 gap-2">
                <Input
                  value={w.start ?? ''}
                  onChange={(e) => updateWindow(idx, { start: e.target.value })}
                  placeholder="09:00"
                  aria-label={`Window ${idx + 1} start`}
                  className="h-8 text-sm font-mono"
                />
                <Input
                  value={w.end ?? ''}
                  onChange={(e) => updateWindow(idx, { end: e.target.value })}
                  placeholder="17:00"
                  aria-label={`Window ${idx + 1} end`}
                  className="h-8 text-sm font-mono"
                />
              </div>
              <DaysPicker
                value={w.days}
                onChange={(v) => updateWindow(idx, { days: v })}
              />
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-8 px-2 text-red-500 hover:bg-red-50"
              onClick={() => removeWindow(idx)}
              aria-label={`Remove window ${idx + 1}`}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      ))}
      <Button type="button" variant="outline" size="sm" className="h-8 text-xs" onClick={addWindow}>
        <Plus className="h-3 w-3 mr-1" /> Add window
      </Button>
    </div>
  );
}

export function AutoRotateConfig({ config, onChange }: { config: Cfg; onChange: (c: Cfg) => void }) {
  const count = getNum(config, 'count', 3) ?? 3;
  return (
    <div className="space-y-3">
      <Field
        label="Slot count"
        htmlFor="cfg-ar-count"
        required
        hint="2–20. Edges: slot_0 … slot_<count-1>, plus default."
      >
        <Input
          id="cfg-ar-count"
          type="number"
          min={2}
          max={20}
          value={count}
          onChange={(e) => onChange({ ...config, count: Number(e.target.value) })}
          className="h-8 w-28 text-sm font-mono"
        />
      </Field>
    </div>
  );
}

// ── Education & engagement ───────────────────────────────────────────────────

export function EnrollmentConfig({ config, onChange }: { config: Cfg; onChange: (c: Cfg) => void }) {
  const courseId = getStr(config, 'course_id');
  const { data, isLoading } = useQuery({
    queryKey: ['courses', 'picker'],
    queryFn: () => courses.list({ limit: 200, active: true }),
    staleTime: 60_000,
  });
  return (
    <Field label="Course" htmlFor="cfg-enr-course" required>
      <Select value={courseId} onValueChange={(v) => onChange({ ...config, course_id: v })}>
        <SelectTrigger id="cfg-enr-course" className="h-8 text-sm">
          <SelectValue placeholder={isLoading ? 'Loading…' : 'Pick a course…'} />
        </SelectTrigger>
        <SelectContent>
          {(data?.rows ?? []).map((c) => (
            <SelectItem key={c.id} value={c.id} className="text-sm">
              <span className="flex items-center gap-2">
                <span className="font-mono text-[11px] text-slate-500">{formatRef(c.ref_number, 'CRS')}</span>
                {c.title}
              </span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </Field>
  );
}

export function SurveyConfig({ config, onChange }: { config: Cfg; onChange: (c: Cfg) => void }) {
  const surveyId = getStr(config, 'survey_id');
  const { data, isLoading } = useQuery({
    queryKey: ['surveys', 'picker'],
    queryFn: () => surveys.list({ limit: 200 }),
    staleTime: 60_000,
  });
  // Only voice surveys are meaningful in an IVR branch
  const voiceSurveys = (data?.rows ?? []).filter((s) => s.kind === 'voice');
  return (
    <div className="space-y-3">
      <Field label="Survey" htmlFor="cfg-survey" required hint="Only voice surveys are listed">
        <Select value={surveyId} onValueChange={(v) => onChange({ ...config, survey_id: v })}>
          <SelectTrigger id="cfg-survey" className="h-8 text-sm">
            <SelectValue placeholder={isLoading ? 'Loading…' : 'Pick a voice survey…'} />
          </SelectTrigger>
          <SelectContent>
            {voiceSurveys.map((s) => (
              <SelectItem key={s.id} value={s.id} className="text-sm">
                <span className="flex items-center gap-2">
                  <span className="font-mono text-[11px] text-slate-500">{formatRef(s.ref_number, 'SRV')}</span>
                  {s.name}
                  <span className="text-[10px] rounded border border-slate-200 bg-slate-50 px-1 py-0.5 text-slate-500">
                    {s.question_count}q
                  </span>
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Field>
      <Field label="DTMF timeout (ms)" htmlFor="cfg-survey-timeout" hint="Default 8000">
        <Input
          id="cfg-survey-timeout"
          type="number"
          min={500}
          max={60000}
          step={500}
          value={getNum(config, 'dtmf_timeout_ms', 8000)}
          onChange={(e) => onChange({ ...config, dtmf_timeout_ms: Number(e.target.value) })}
          className="h-8 w-32 text-sm font-mono"
        />
      </Field>
      <div className="flex items-center gap-3">
        <Switch
          id="cfg-survey-skip"
          checked={getBool(config, 'skip_required_on_timeout')}
          onCheckedChange={(v) => onChange({ ...config, skip_required_on_timeout: v })}
          aria-label="Skip required on timeout"
        />
        <Label htmlFor="cfg-survey-skip" className="text-xs text-slate-600">
          Skip required questions on DTMF timeout (default: abandon)
        </Label>
      </div>
    </div>
  );
}

export function QuizConfig({ config, onChange }: { config: Cfg; onChange: (c: Cfg) => void }) {
  const quizId = getStr(config, 'quiz_id');
  const { data, isLoading } = useQuery({
    queryKey: ['quizzes', 'picker'],
    queryFn: () => quizzes.list({ limit: 200 }),
    staleTime: 60_000,
  });
  return (
    <Field label="Quiz" htmlFor="cfg-quiz" required>
      <Select value={quizId} onValueChange={(v) => onChange({ ...config, quiz_id: v })}>
        <SelectTrigger id="cfg-quiz" className="h-8 text-sm">
          <SelectValue placeholder={isLoading ? 'Loading…' : 'Pick a quiz…'} />
        </SelectTrigger>
        <SelectContent>
          {(data?.rows ?? []).map((q) => (
            <SelectItem key={q.id} value={q.id} className="text-sm">
              <span className="flex items-center gap-2">
                <span className="font-mono text-[11px] text-slate-500">{formatRef(q.ref_number, 'QZ')}</span>
                {q.name}
                <span className="text-[10px] rounded border border-slate-200 bg-slate-50 px-1 py-0.5 text-slate-500">
                  {q.pass_threshold_pct}% pass
                </span>
              </span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </Field>
  );
}

export function PointsConfig({ config, onChange }: { config: Cfg; onChange: (c: Cfg) => void }) {
  const delta = getNum(config, 'delta', 0) ?? 0;
  return (
    <div className="space-y-3">
      <Field
        label="Delta (points)"
        htmlFor="cfg-pts-delta"
        required
        hint="Positive to award, negative to debit. Debit below zero routes to 'insufficient'."
      >
        <Input
          id="cfg-pts-delta"
          type="number"
          value={delta}
          onChange={(e) => onChange({ ...config, delta: Number(e.target.value) })}
          className="h-8 w-32 text-sm font-mono"
        />
      </Field>
      <Field label="Reason" htmlFor="cfg-pts-reason" hint="Shown in the caller's ledger">
        <Input
          id="cfg-pts-reason"
          value={getStr(config, 'reason')}
          onChange={(e) => onChange({ ...config, reason: e.target.value })}
          placeholder="module_1_complete"
          className="h-8 text-sm font-mono"
          maxLength={500}
        />
      </Field>
      <Field label="Source kind" htmlFor="cfg-pts-source">
        <Select
          value={getStr(config, 'source_kind', 'ivr_node')}
          onValueChange={(v) => onChange({ ...config, source_kind: v })}
        >
          <SelectTrigger id="cfg-pts-source" className="h-8 text-sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {['ivr_node', 'quiz', 'enrollment', 'admin', 'redeem', 'correction', 'survey', 'forum'].map((k) => (
              <SelectItem key={k} value={k} className="text-sm font-mono">{k}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Field>
    </div>
  );
}

// ── Admin & advanced ─────────────────────────────────────────────────────────

export function AdminConfig({ config, onChange }: { config: Cfg; onChange: (c: Cfg) => void }) {
  return (
    <div className="space-y-3">
      <p className="text-[11px] text-slate-500">
        Breadcrumb node — logs an audit event when a caller reaches it. Does
        not perform any privileged action on its own.
      </p>
      <Field label="Action label" htmlFor="cfg-admin-action" hint="Optional free-form string logged with the event">
        <Input
          id="cfg-admin-action"
          value={getStr(config, 'action')}
          onChange={(e) => onChange({ ...config, action: e.target.value })}
          placeholder="escalation_page"
          className="h-8 text-sm font-mono"
          maxLength={200}
        />
      </Field>
    </div>
  );
}

export function BackConfig() {
  return (
    <div className="rounded-md bg-blue-50/60 border border-blue-100 px-3 py-2.5 text-xs text-blue-700">
      Pops one level off the navigation stack. No configuration — the runtime
      manages a 20-deep back-stack per call.
    </div>
  );
}

export function RepeatConfig() {
  return (
    <div className="rounded-md bg-blue-50/60 border border-blue-100 px-3 py-2.5 text-xs text-blue-700">
      Replays the node's main recording. No configuration — uses the node's
      main recording or main prompt key.
    </div>
  );
}

export function CustomConfig({ config, onChange }: { config: Cfg; onChange: (c: Cfg) => void }) {
  const payloadObj = (typeof config.payload === 'object' && config.payload !== null)
    ? (config.payload as Cfg)
    : {};
  const [payloadText, setPayloadText] = useState(() => JSON.stringify(payloadObj, null, 2));
  const [payloadError, setPayloadError] = useState('');

  function handlePayloadBlur() {
    try {
      const parsed = JSON.parse(payloadText);
      if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
        setPayloadError('Payload must be a JSON object.');
        return;
      }
      onChange({ ...config, payload: parsed });
      setPayloadError('');
    } catch {
      setPayloadError('Invalid JSON.');
    }
  }

  return (
    <div className="space-y-3">
      <Field
        label="Webhook URL"
        htmlFor="cfg-custom-url"
        required
        hint="HTTPS only. Private IPs / localhost are rejected."
      >
        <Input
          id="cfg-custom-url"
          type="url"
          value={getStr(config, 'url')}
          onChange={(e) => onChange({ ...config, url: e.target.value })}
          placeholder="https://hooks.example.com/ivrello"
          className="h-8 text-sm font-mono"
        />
      </Field>
      <Field label="Timeout (ms)" htmlFor="cfg-custom-timeout">
        <Input
          id="cfg-custom-timeout"
          type="number"
          min={100}
          max={60000}
          step={100}
          value={getNum(config, 'timeout_ms', 5000)}
          onChange={(e) => onChange({ ...config, timeout_ms: Number(e.target.value) })}
          className="h-8 w-32 text-sm font-mono"
        />
      </Field>
      <Field label="Payload JSON" htmlFor="cfg-custom-payload" hint="Free-form object merged into the outgoing POST body">
        <Textarea
          id="cfg-custom-payload"
          value={payloadText}
          onChange={(e) => setPayloadText(e.target.value)}
          onBlur={handlePayloadBlur}
          rows={5}
          placeholder='{ "event": "caller_escalation" }'
          className="text-xs font-mono resize-y"
        />
        {payloadError && <p role="alert" className="text-[11px] text-red-500">{payloadError}</p>}
      </Field>
    </div>
  );
}

// ── Auth & access ────────────────────────────────────────────────────────────

/**
 * Config panel for the auth_list_signup IVR node.
 *
 * Edges:
 *   success    — registration succeeded
 *   duplicate  — number already in list
 *   failed     — config / DB error
 *   cancelled  — caller pressed cancel at confirmation
 */
export function AuthListSignupConfig({ config, onChange }: { config: Cfg; onChange: (c: Cfg) => void }) {
  const authListId = getStr(config, 'auth_list_id');
  const requireConfirmation = getBool(config, 'require_confirmation', true);
  const allowEnterDifferentNumber = getBool(config, 'allow_enter_different_number', false);

  const { data, isLoading } = useQuery({
    queryKey: ['authLists', 'signup-picker'],
    queryFn: () => authLists.list(),
    staleTime: 60_000,
  });

  // Only allowlists and pin_lists support self-registration.
  const eligible = (data?.rows ?? []).filter(
    (r) => r.kind === 'allowlist' || r.kind === 'pin_list',
  );

  return (
    <div className="space-y-4">
      {/* Auth list selector */}
      <Field label="Target auth list" htmlFor="cfg-als-list" required hint="Only allowlists and PIN lists can accept self-registrations.">
        <Select
          value={authListId || '__none__'}
          onValueChange={(v) => onChange({ ...config, auth_list_id: v === '__none__' ? '' : v })}
          disabled={isLoading}
        >
          <SelectTrigger id="cfg-als-list" className="h-8 text-sm">
            <SelectValue placeholder={isLoading ? 'Loading…' : 'Pick an auth list…'} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__none__" className="text-sm text-muted-foreground">
              — pick a list —
            </SelectItem>
            {eligible.map((l) => (
              <SelectItem key={l.id} value={l.id} className="text-sm">
                <span className="flex items-center gap-2">
                  <span className="text-[10px] rounded border border-slate-200 bg-slate-50 px-1 py-0.5 text-slate-500 font-mono uppercase">
                    {l.kind === 'pin_list' ? 'pin' : 'allow'}
                  </span>
                  {l.name}
                  <span className="font-mono text-[10px] text-muted-foreground">
                    {l.entry_count}
                  </span>
                </span>
              </SelectItem>
            ))}
            {eligible.length === 0 && !isLoading && (
              <div className="px-2 py-3 text-xs text-muted-foreground">
                No allowlists or PIN lists found.
              </div>
            )}
          </SelectContent>
        </Select>
      </Field>

      {/* Toggles */}
      <div className="space-y-3">
        <div className="flex items-center gap-3">
          <Switch
            id="cfg-als-confirm"
            checked={requireConfirmation}
            onCheckedChange={(v) => onChange({ ...config, require_confirmation: v })}
          />
          <Label htmlFor="cfg-als-confirm" className="text-xs cursor-pointer">
            Require confirmation
            <span className="ml-1 text-slate-400 font-normal">
              — ask caller to press 1 to confirm before inserting
            </span>
          </Label>
        </div>
        <div className="flex items-center gap-3">
          <Switch
            id="cfg-als-enter-num"
            checked={allowEnterDifferentNumber}
            onCheckedChange={(v) => onChange({ ...config, allow_enter_different_number: v })}
          />
          <Label htmlFor="cfg-als-enter-num" className="text-xs cursor-pointer">
            Allow entering a different number
            <span className="ml-1 text-slate-400 font-normal">
              — if off, the caller&apos;s own caller ID is registered automatically
            </span>
          </Label>
        </div>
      </div>

      {/* Edge reference */}
      <div className="rounded-md border border-slate-100 bg-slate-50/60 px-3 py-2.5 space-y-1.5">
        <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">
          Available edges
        </p>
        {[
          { key: 'success',   desc: 'Caller was successfully registered' },
          { key: 'duplicate', desc: 'Number already exists in the list' },
          { key: 'failed',    desc: 'Config or database error' },
          { key: 'cancelled', desc: 'Caller pressed cancel at confirmation' },
        ].map(({ key, desc }) => (
          <div key={key} className="flex items-start gap-2 text-xs">
            <code className="shrink-0 rounded bg-slate-200/70 px-1 py-0.5 font-mono text-[10px] text-slate-700">
              {key}
            </code>
            <span className="text-slate-500">{desc}</span>
          </div>
        ))}
        <p className="text-[10px] text-slate-400 pt-0.5">
          Wire these edges in the IVR tree to route the caller after each outcome.
        </p>
      </div>
    </div>
  );
}

// ── Generic fallback ─────────────────────────────────────────────────────────

export function GenericJsonConfig({ config, onChange }: { config: Cfg; onChange: (c: Cfg) => void }) {
  const [text, setText] = useState(() => JSON.stringify(config, null, 2));
  const [jsonError, setJsonError] = useState('');

  function handleBlur() {
    try {
      onChange(JSON.parse(text));
      setJsonError('');
    } catch {
      setJsonError('Invalid JSON — fix before saving.');
    }
  }

  return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-1.5 text-xs text-slate-500">
        <Clock className="h-3.5 w-3.5" />
        No typed form yet for this node type. Free-form JSON accepted for now.
      </div>
      <Textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        onBlur={handleBlur}
        className="font-mono text-xs"
        rows={6}
        aria-label="Node config JSON"
      />
      {jsonError && <p role="alert" className="text-xs text-red-500">{jsonError}</p>}
    </div>
  );
}

// ── Type registry ────────────────────────────────────────────────────────────

/**
 * Node types that now have a typed form. The NodeEditorDrawer checks this set
 * to decide whether to render the MVP badge and whether to drop to the
 * GenericJsonConfig fallback.
 */
export const TYPED_NODE_TYPES = new Set<IvrNodeType>([
  // MVP (already-existing)
  'playback', 'menu', 'transfer', 'jump',
  // Content & navigation
  'series_folder', 'sequential_folder',
  'unlock_series_folder', 'unlock_sequential_folder',
  'bulletin', 'latest_n_folder',
  'rotation_folder', 'random_folder', 'bookmark', 'return_to_bookmark',
  // Input & logic
  'data_collection', 'password', 'label_route', 'add_label', 'remove_label',
  'modify_auth', 'modify_audio', 'playback_settings',
  // Communication & actions
  'record', 'voicemail', 'listen_voicemail', 'conference',
  'ring_group', 'dial', 'sip_connect', 'extension',
  'notify_branch', 'public_forum',
  // Payments
  'payment', 'shopping',
  // Scheduling
  'day_of_week', 'timer', 'switch_timer', 'auto_rotate',
  // Education & engagement
  'enrollment', 'survey', 'quiz', 'points',
  // Admin & advanced
  'admin', 'back', 'repeat', 'custom',
  // Auth & access
  'auth_list_signup',
]);

/**
 * Required-field validator for a node type's config. Returns a map of
 * error messages keyed by field for display. Keep shallow — deeper
 * validation lives on the backend Zod schema.
 */
export function validateConfigForType(
  nodeType: IvrNodeType,
  config: Cfg,
): Record<string, string> {
  const errs: Record<string, string> = {};
  const str = (k: string) => (typeof config[k] === 'string' ? (config[k] as string).trim() : '');

  switch (nodeType) {
    case 'series_folder':
    case 'sequential_folder':
    case 'unlock_series_folder':
    case 'unlock_sequential_folder':
    case 'bulletin':
    case 'latest_n_folder':
    case 'rotation_folder':
    case 'random_folder':
      if (!str('folder_id')) errs.folder_id = 'Folder ID is required.';
      break;
    case 'data_collection':
      if (!str('field_key')) errs.field_key = 'Field key is required.';
      break;
    case 'voicemail':
    case 'listen_voicemail':
      if (!str('voicemail_box_id')) errs.voicemail_box_id = 'Voicemail box is required.';
      break;
    case 'conference':
      if (!str('conference_room_id')) errs.conference_room_id = 'Conference room is required.';
      break;
    case 'ring_group':
      if (!str('ring_group_id')) errs.ring_group_id = 'Ring group is required.';
      break;
    case 'dial':
      if (!str('target')) errs.target = 'Target is required.';
      break;
    case 'sip_connect': {
      const uri = str('uri');
      if (!uri) errs.uri = 'URI is required.';
      else if (!/^(sip:|sips:|sofia\/)/.test(uri)) errs.uri = 'Must start with sip:, sips:, or sofia/.';
      break;
    }
    case 'extension':
      if (!str('extension_id')) errs.extension_id = 'Extension is required.';
      break;
    case 'notify_branch':
      if (!str('to')) errs.to = 'Recipient is required.';
      if (!str('body')) errs.body = 'Message body is required.';
      break;
    case 'payment': {
      const amt = config.amount_cents;
      if (typeof amt !== 'number' || amt <= 0) errs.amount_cents = 'Amount must be greater than zero.';
      break;
    }
    case 'timer':
      if (!/^([01]\d|2[0-3]):[0-5]\d$/.test(str('start'))) errs.start = 'Start must be HH:MM.';
      if (!/^([01]\d|2[0-3]):[0-5]\d$/.test(str('end'))) errs.end = 'End must be HH:MM.';
      break;
    case 'switch_timer': {
      const ws = Array.isArray(config.windows) ? config.windows as Array<{ name?: string; start?: string; end?: string }> : [];
      if (ws.length === 0) errs.windows = 'At least one window is required.';
      for (const [i, w] of ws.entries()) {
        if (!w.name?.trim()) errs.windows = `Window ${i + 1} needs a name.`;
        if (!w.start || !/^([01]\d|2[0-3]):[0-5]\d$/.test(w.start)) errs.windows = `Window ${i + 1} start must be HH:MM.`;
        if (!w.end || !/^([01]\d|2[0-3]):[0-5]\d$/.test(w.end)) errs.windows = `Window ${i + 1} end must be HH:MM.`;
      }
      break;
    }
    case 'auto_rotate': {
      const c = config.count;
      if (typeof c !== 'number' || c < 2 || c > 20) errs.count = 'Count must be between 2 and 20.';
      break;
    }
    case 'enrollment':
      if (!str('course_id')) errs.course_id = 'Course is required.';
      break;
    case 'survey':
      if (!str('survey_id')) errs.survey_id = 'Survey is required.';
      break;
    case 'quiz':
      if (!str('quiz_id')) errs.quiz_id = 'Quiz is required.';
      break;
    case 'points':
      if (typeof config.delta !== 'number' || config.delta === 0) errs.delta = 'Delta is required (and non-zero).';
      break;
    case 'add_label':
    case 'remove_label':
      if (!str('label_id')) errs.label_id = 'Label ID is required.';
      break;
    case 'custom':
      if (!str('url')) errs.url = 'Webhook URL is required.';
      else if (!/^https:\/\//.test(str('url'))) errs.url = 'HTTPS is required.';
      break;
    case 'password': {
      if (!str('password_hash') && !str('password')) {
        errs.password_hash = 'Either a bcrypt hash or a plaintext password is required.';
      }
      break;
    }
    case 'auth_list_signup':
      if (!str('auth_list_id')) errs.auth_list_id = 'Auth list is required.';
      break;
    // Remaining: no per-type required fields
  }
  return errs;
}

// ── Master dispatch ──────────────────────────────────────────────────────────

export interface TypedConfigFormProps {
  nodeType: IvrNodeType;
  config: Cfg;
  onChange: (c: Cfg) => void;
  /** Optional UUID of the node being edited. Used by PublicForumConfig
   *  (forum link) and PaymentConfig (excluded from the decline-routing
   *  node picker). */
  nodeId?: string;
  /** Optional ref_number of the node — preferred over the UUID when
   *  building human-readable URLs (Sam 2026-05-21). */
  nodeRefNumber?: number | null;
  /** All nodes of the surrounding tree. NodeEditorDrawer passes its
   *  treeNodes prop through so per-type forms can offer tree-scoped
   *  node pickers (rule 13) — currently the payment decline-routing
   *  editor. Optional so type-only consumers don't have to care. */
  treeNodes?: IvrNodeRow[];
}

/**
 * Renders a typed form for the given node_type. Types unknown to this
 * dispatcher fall through to GenericJsonConfig. Note: a few types
 * (playback, menu, transfer, jump) are still rendered inline in
 * NodeEditorDrawer and intentionally *not* dispatched here to preserve
 * the existing maxReprompt/wiring linkage on the menu form.
 */
export function TypedConfigForm({
  nodeType,
  config,
  onChange,
  nodeId,
  nodeRefNumber,
  treeNodes,
}: TypedConfigFormProps) {
  switch (nodeType) {
    // Content & navigation
    case 'series_folder': return <SeriesFolderConfig config={config} onChange={onChange} />;
    case 'sequential_folder': return <SequentialFolderConfig config={config} onChange={onChange} />;
    case 'unlock_series_folder': return <UnlockSeriesFolderConfig config={config} onChange={onChange} />;
    case 'unlock_sequential_folder': return <UnlockSequentialFolderConfig config={config} onChange={onChange} />;
    case 'bulletin': return <BulletinConfig config={config} onChange={onChange} />;
    case 'latest_n_folder': return <LatestNFolderConfig config={config} onChange={onChange} />;
    case 'rotation_folder': return <RotationFolderConfig config={config} onChange={onChange} nodeId={nodeId} />;
    case 'random_folder': return <RandomFolderConfig config={config} onChange={onChange} />;
    case 'bookmark': return <BookmarkConfig config={config} onChange={onChange} />;
    case 'return_to_bookmark': return <ReturnToBookmarkConfig />;

    // Input & logic
    case 'data_collection': return <DataCollectionConfig config={config} onChange={onChange} />;
    case 'password': return <PasswordNodeConfig config={config} onChange={onChange} />;
    case 'label_route': return <LabelRouteConfig config={config} onChange={onChange} />;
    case 'add_label': return <AddLabelConfig config={config} onChange={onChange} />;
    case 'remove_label': return <RemoveLabelConfig config={config} onChange={onChange} />;
    case 'modify_auth': return <ModifyAuthConfig config={config} onChange={onChange} />;
    case 'modify_audio': return <ModifyAudioConfig config={config} onChange={onChange} />;
    case 'playback_settings': return <PlaybackSettingsConfig />;

    // Communication & actions
    case 'record': return <RecordConfig config={config} onChange={onChange} />;
    case 'voicemail': return <VoicemailConfig config={config} onChange={onChange} />;
    case 'listen_voicemail': return <ListenVoicemailConfig config={config} onChange={onChange} />;
    case 'conference': return <ConferenceConfig config={config} onChange={onChange} />;
    case 'ring_group': return <RingGroupConfig config={config} onChange={onChange} />;
    case 'dial': return <DialConfig config={config} onChange={onChange} />;
    case 'sip_connect': return <SipConnectConfig config={config} onChange={onChange} />;
    case 'extension': return <ExtensionNodeConfig config={config} onChange={onChange} />;
    case 'notify_branch': return <NotifyBranchConfig config={config} onChange={onChange} />;
    case 'public_forum': return <PublicForumConfig config={config} onChange={onChange} nodeId={nodeId} nodeRefNumber={nodeRefNumber ?? null} />;

    // Payments
    case 'payment': return <PaymentConfig config={config} onChange={onChange} treeNodes={treeNodes} currentNodeId={nodeId} />;
    case 'shopping': return <ShoppingConfig config={config} onChange={onChange} />;

    // Scheduling
    case 'day_of_week': return <DayOfWeekConfig config={config} onChange={onChange} />;
    case 'timer': return <TimerConfig config={config} onChange={onChange} />;
    case 'switch_timer': return <SwitchTimerConfig config={config} onChange={onChange} />;
    case 'auto_rotate': return <AutoRotateConfig config={config} onChange={onChange} />;

    // Education & engagement
    case 'enrollment': return <EnrollmentConfig config={config} onChange={onChange} />;
    case 'survey': return <SurveyConfig config={config} onChange={onChange} />;
    case 'quiz': return <QuizConfig config={config} onChange={onChange} />;
    case 'points': return <PointsConfig config={config} onChange={onChange} />;

    // Admin & advanced
    case 'admin': return <AdminConfig config={config} onChange={onChange} />;
    case 'back': return <BackConfig />;
    case 'repeat': return <RepeatConfig />;
    case 'custom': return <CustomConfig config={config} onChange={onChange} />;

    // Auth & access
    case 'auth_list_signup': return <AuthListSignupConfig config={config} onChange={onChange} />;

    default:
      return <GenericJsonConfig config={config} onChange={onChange} />;
  }
}

